import type { Messages } from './en.js';

export const es: Messages = {
  app: {
    name: 'Vammo Work',
    fixtureBanner: 'Entorno de fixtures — datos de ejemplo, sin integraciones reales y sin mensajes reales.',
    skipToContent: 'Ir al contenido',
  },
  nav: {
    documents: 'Documentos',
    myWork: 'Mi trabajo',
    requests: 'Solicitudes',
    work: 'Trabajo',
    projects: 'Proyectos',
    teams: 'Equipos',
    reports: 'Reportes',
    admin: 'Administración',
    settings: 'Configuración',
    newRequest: 'Nueva solicitud',
    newWork: 'Nuevo ítem de trabajo',
    search: 'Buscar',
    searchPlaceholder: 'Busca trabajo, solicitudes y documentos o escribe una clave…',
    notifications: 'Notificaciones',
    collapse: 'Contraer menú',
    expand: 'Expandir menú',
    openMenu: 'Abrir navegación',
    signOut: 'Cerrar sesión',
    theme: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    themeSystem: 'Sistema',
    autoTranslate: 'Traducción automática',
    language: 'Idioma',
    shortcuts: 'Atajos de teclado',
    tutorial: 'Tutorial',
  },
  repoPicker: {
    workTitle: 'Repositorios vinculados',
    workHint: 'Vincular repositorios agrega evidencia para el trabajo futuro. Los episodios completados conservan su base original.',
    projectTitle: 'Repositorios del proyecto',
    projectHint: 'La reconciliación y la cobertura de captura incluyen los repositorios adjuntos aquí.',
    search: 'Buscar repositorios',
    noneSelected: 'No hay repositorios seleccionados.',
    remove: 'Quitar {{name}}',
    archived: 'Archivado',
    edit: 'Editar repositorios',
  },
  translationReport: {
    action: 'Reportar',
    title: 'Reportar esta traducción',
    hint: 'Cuéntanos qué está mal. El texto original nunca se modifica.',
    comment: '¿Qué está mal? (opcional)',
    regenerate: 'Traducir de nuevo',
    submit: 'Enviar reporte',
    thanks: 'Gracias, se registró el reporte',
  },
  languageCorrection: {
    label: 'Idioma original',
    hint: 'Corrígelo si el texto se detectó en el idioma equivocado; las traducciones se recrean.',
  },
  conflict: {
    title: 'Alguien guardó antes',
    hint: 'Compara tu cambio con la versión actual y elige cuál conservar.',
    field: 'Campo',
    mine: 'Tu cambio',
    current: 'Versión actual',
    applyMine: 'Aplicar mi cambio',
    keepCurrent: 'Conservar la versión actual',
    fields: { title: 'Título', descriptionMarkdown: 'Descripción', acceptanceCriteria: 'Criterios de aceptación', priority: 'Prioridad', assigneeId: 'Responsable', labels: 'Etiquetas', type: 'Tipo', originalLocale: 'Idioma original', repositoryIds: 'Repositorios', problemMarkdown: 'Problema', expectedOutcome: 'Resultado esperado' },
  },
  common: {
    removeItem: 'Quitar {{name}}',
    viewPersonWork: 'Ver las tareas de {{name}}',
    save: 'Guardar',
    saving: 'Guardando…',
    cancel: 'Cancelar',
    close: 'Cerrar',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    archive: 'Archivar',
    confirm: 'Confirmar',
    submit: 'Enviar',
    back: 'Volver',
    next: 'Siguiente',
    retry: 'Reintentar',
    reload: 'Recargar',
    loading: 'Cargando…',
    none: 'Ninguno',
    unassigned: 'Sin asignar',
    unknown: 'Desconocido',
    optional: 'opcional',
    required: 'Obligatorio',
    yes: 'Sí',
    no: 'No',
    all: 'Todos',
    me: 'Yo',
    filter: 'Filtrar',
    clearFilters: 'Limpiar filtros',
    sort: 'Ordenar',
    openFullPage: 'Abrir página completa',
    copyLink: 'Copiar enlace',
    linkCopied: 'Enlace copiado',
    viewOriginal: 'Ver original',
    viewTranslation: 'Ver traducción',
    machineTranslated: 'Traducción automática del {{language}}',
    translating: 'Traduciendo…',
    translateItem: 'Traducir',
    translateItemHint: 'Traducir este elemento a tu idioma',
    showOriginalItem: 'Ver original',
    translationFailed: 'Traducción no disponible; se muestra el original.',
    reportTranslation: 'Reportar traducción',
    noResults: 'Sin resultados',
    comingSoon: 'Aún no disponible',
    reason: 'Motivo',
    reasonPlaceholder: 'Explica la decisión',
    created: 'Creado',
    updated: 'Actualizado',
    by: 'por {{name}}',
    you: 'tú',
    changesSaved: 'Cambios guardados',
    actionFailed: 'La acción falló',
    count_one: '{{count}} elemento',
    count_other: '{{count}} elementos',
    preview: 'Vista previa',
    write: 'Escribir',
    markdownHint: 'Admite Markdown: **negrita**, _cursiva_, `código`, listas y enlaces.',
    unsavedChanges: 'Tienes cambios sin guardar. Guárdalos o cancélalos primero.',
    discard: 'Descartar',
    keepEditing: 'Seguir editando',
  },
  errors: {
    title: 'Algo salió mal',
    notFound: 'No se encontró, o no tienes acceso.',
    forbidden: 'No tienes permiso para hacer eso.',
    unauthenticated: 'Tu sesión terminó. Inicia sesión de nuevo para continuar.',
    conflict: 'Otra persona cambió esto mientras tanto. Revisa la versión más reciente y vuelve a aplicar tu edición.',
    staleGeneration: 'Existe una entrega más reciente. Revisa el estado actual antes de responder.',
    rateLimited: 'Demasiadas solicitudes. Espera un momento.',
    unavailable: 'Esta funcionalidad no está disponible en este momento.',
    offline: 'Parece que no tienes conexión. Los cambios no se guardarán hasta que vuelva.',
    validation: 'Corrige los campos resaltados.',
    generic: 'Error inesperado. Referencia: {{correlationId}}',
  },
  auth: {
    signIn: 'Iniciar sesión con Vammo',
    signingIn: 'Redirigiendo al inicio de sesión…',
    errorTitle: 'El inicio de sesión no se completó',
    errorState: 'El enlace de inicio de sesión expiró o ya se usó. Empieza de nuevo.',
    errorSuspended: 'Tu cuenta está suspendida. Contacta a un administrador.',
    errorGeneric: 'No pudimos verificar tu inicio de sesión corporativo.',
    tryAgain: 'Reintentar',
  },
  status: {
    request: {
      DRAFT: 'Borrador',
      SUBMITTED: 'Enviada',
      NEEDS_INFORMATION: 'Requiere información',
      ACCEPTED: 'Aceptada',
      REJECTED: 'Rechazada',
      DUPLICATE: 'Duplicada',
      RESOLVED: 'Resuelta',
      NEEDS_ATTENTION: 'Requiere atención',
      CANCELED: 'Cancelada',
    },
    phase: {
      TODO: 'Por hacer',
      IN_PROGRESS: 'En curso',
      IN_REVIEW: 'En revisión',
      INTEGRATED: 'Integrado',
      DONE: 'Terminado',
      CANCELED: 'Cancelado',
      EXPIRED: 'Expirada',
    },
    priority: { URGENT: 'Urgente', HIGH: 'Alta', NORMAL: 'Normal', LOW: 'Baja' },
    workType: {
      FEATURE: 'Funcionalidad',
      BUG: 'Bug',
      MAINTENANCE: 'Mantenimiento',
      INFRASTRUCTURE: 'Infraestructura',
      RESEARCH: 'Investigación',
      ANALYSIS: 'Análisis',
      OPERATIONS: 'Operaciones',
      REVIEW: 'Revisión',
      OTHER: 'Otro',
      EPIC: 'Épica',
    },
    policy: { PRODUCTION: 'Entrega en producción', DELIVERABLE: 'Entregable', REVIEWED_DELIVERABLE: 'Entregable revisado' },
    policyHelp: {
      PRODUCTION: 'Terminado solo cuando cada cambio requerido está verificado en producción.',
      DELIVERABLE: 'Terminado cuando la persona responsable registra el resultado frente a los criterios.',
      REVIEWED_DELIVERABLE: 'Terminado cuando un revisor independiente aprueba el resultado.',
    },
    delivery: { UNKNOWN: 'Entrega desconocida', NOT_DEPLOYED: 'No desplegado', PARTIAL: 'Desplegado parcialmente', DEPLOYED: 'Desplegado', REVERTED: 'Revertido' },
    association: { CONFIRMED: 'Confirmado', PROVISIONAL: 'Provisional' },
    visibility: { COMPANY: 'Empresa', RESTRICTED: 'Restringido' },
    visibilityHelp: { COMPANY: 'Visible para todas las personas de Vammo con sesión iniciada.', RESTRICTED: 'Visible solo para las personas, equipos y grupos seleccionados (y Superadmins).' },
    basis: { AGENT_REPORTED: 'Reportado por agente', HUMAN_REPORTED: 'Reportado', PROVIDER_VERIFIED: 'Verificado', IMPORTED_SOURCE: 'Importado', FIXTURE: 'Fixture' },
    completionBasis: { VERIFIED_PRODUCTION: 'Verificado en producción', DELIVERABLE: 'Entregable registrado', REVIEWED_DELIVERABLE: 'Revisado y aprobado', IMPORTED_SOURCE: 'Terminado en Jira (importado)', UNVERIFIED: 'Cerrado sin evidencia' },
    confirmation: { UNCONFIRMED: 'Esperando tu confirmación', CONFIRMED: 'Confirmado como resuelto', PERSISTS: 'Sigue ocurriendo' },
    fulfillment: {
      NOT_STARTED: 'Sin empezar',
      IN_PROGRESS: 'En curso',
      IN_REVIEW: 'En revisión',
      AWAITING_DEPLOYMENT: 'Esperando despliegue en producción',
      AWAITING_ACTIVATION: 'Esperando activación',
      DELIVERED: 'Entregado',
      NONE_LINKED: 'Aún no planificado',
    },
    channel: { WEB: 'Web', SLACK: 'Slack', MCP: 'Asistente de IA', API: 'API', EMAIL: 'Correo', JIRA_IMPORT: 'Importación de Jira', SYSTEM: 'Sistema', VAMMO_APPS: 'Vammo Apps' },
    scope: { NONE: 'No aplica', AWAITING_SCOPE: 'Esperando alcance de entrega', UNCONFIRMED: 'Alcance sin confirmar', CONFIRMED: 'Alcance confirmado' },
    scopeAutomatic: 'Derivado del código vinculado y el mapeo de servicios',
    scopeNotAffected: 'No afectados por este cambio',
    milestone: { START: 'Iniciado', SCOPE_CHANGE: 'Cambio de alcance', BLOCKER: 'Bloqueo', VERIFICATION: 'Verificación', PR_UPDATE: 'Pull request', HANDOFF: 'Traspaso', SESSION_END: 'Sesión finalizada', NOTE: 'Nota' },
    client: { CLAUDE_CODE: 'Claude Code', CODEX: 'Codex', CLAUDE: 'Claude', CHATGPT: 'ChatGPT', CLAWBOT: 'Clawbot (Slack)', OTHER: 'Otro cliente' },
  },
  actions: {
    request: {
      submit: 'Enviar solicitud',
      cancel: 'Cancelar solicitud',
      ask_information: 'Pedir información',
      answer_information: 'Enviar respuesta',
      accept: 'Aceptar',
      reject: 'Rechazar',
      mark_duplicate: 'Marcar como duplicada',
      fulfill: 'Marcar como atendida',
      report_persistence: 'Informar que sigue ocurriendo',
      fulfillment_invalidated: 'Reabrir para atención',
      accept_correction: 'Aceptar corrección',
      fulfill_after_review: 'Resolver tras revisión',
      reconsider: 'Reconsiderar',
    },
    work: {
      start: 'Empezar',
      request_review: 'Pedir revisión',
      request_changes: 'Pedir cambios',
      integration_observed: 'Integrado',
      production_verified: 'Verificado en producción',
      complete_deliverable: 'Completar',
      approve_deliverable: 'Aprobar',
      cancel: 'Cancelar trabajo',
      reopen: 'Reabrir',
      coverage_lost: 'Cobertura perdida',
      close_without_evidence: 'Cerrar sin evidencia',
      expire: 'Expirar',
    },
  },
  myWork: {
    title: 'Mi trabajo',
    greeting: 'Hola, {{name}}',
    subtitle: 'Lo que necesita tu atención hoy.',
    assigned: 'Asignado a mí',
    assignedEmpty: 'No tienes nada asignado por ahora.',
    myRequests: 'Mis solicitudes',
    myRequestsEmpty: 'Todavía no registraste solicitudes.',
    needsMe: 'Esperándome',
    needsMeEmpty: 'Nada te está esperando.',
    blocked: 'Bloqueado',
    triageInbox: 'Bandeja de triage',
    updates: 'Novedades recientes',
    updatesEmpty: 'Todavía no hay novedades.',
    following: 'Siguiendo',
    followingEmpty: 'Sigue trabajo o solicitudes para verlos aquí.',
    overdue: 'Vencidos',
    overdueEmpty: 'No tienes nada vencido.',
    dueSoon: 'Vencen en los próximos 7 días',
    dueSoonEmpty: 'No tienes nada que venza esta semana.',
    stats: { open: 'Trabajo abierto', inReview: 'En revisión', blocked: 'Bloqueados', attention: 'Solicitudes que requieren atención', overdue: 'Vencidos' },
  },
  requests: {
    move: { noProject: 'Sin proyecto', title: 'Mover a {{name}}', hint: 'Mover cambia quién puede leer esta solicitud, sus comentarios y archivos.', restrictedAfter: 'Tras moverla, la solicitud quedará restringida a la audiencia del proyecto.', companyAfter: 'Tras moverla, la solicitud será visible para toda la empresa.', losesAccess: 'Estas personas perderían acceso:', nobodyLoses: 'Nadie involucrado pierde acceso.', reassignFirst: 'El responsable perdería acceso. Reasigna la solicitud primero.', confirm: 'Mover solicitud' },
    activation: { pending: 'Activación pendiente:', observed: 'Activación observada por {{name}} el {{time}}', notRequired: 'No requiere una activación aparte.', require: 'Requerir activación', record: 'Registrar activación', waive: 'Eximir activación', requireHint: 'Úsalo cuando desplegar no alcanza, por ejemplo si hay que encender un feature flag. La solicitud queda aceptada hasta registrar la activación.', recordHint: 'Registra quién observó el cambio activo y la evidencia. Queda auditado.', waiveHint: 'La activación dejará de ser obligatoria. El motivo queda auditado.', noteLabel: '¿Qué debe activarse?', evidenceLabel: 'Evidencia', reasonLabel: 'Motivo' },
    duplicates: { title: 'Posibles duplicados', hint: 'Solicitudes similares que puedes ver. Al marcarla, cada solicitante sigue informado.', mark: 'Marcar como duplicada de esta' },
    title: 'Solicitudes',
    subtitle: 'Problemas, ideas y pedidos de toda Vammo.',
    tabs: { inbox: 'Entrada', mine: 'Mías', attention: 'Requieren atención', following: 'Siguiendo', all: 'Todas', snoozed: 'Pospuestas' },
    empty: 'No hay solicitudes aquí.',
    emptyInbox: 'La bandeja está vacía. Las nuevas solicitudes aparecen aquí para triage.',
    columns: { key: 'Clave', title: 'Título', status: 'Estado', priority: 'Prioridad', author: 'Solicitante', assignee: 'Responsable', team: 'Equipo', updated: 'Actualizado', created: 'Creado', source: 'Origen' },
    snooze: {
      action: 'Posponer',
      title: 'Posponer solicitud',
      hint: 'Sale de la bandeja de triage y vuelve sola en el momento elegido. Cualquier cambio de estado la trae de vuelta antes.',
      until: 'Posponer hasta',
      tomorrow: 'Mañana a la mañana',
      nextWeek: 'El próximo lunes',
      inTwoWeeks: 'En dos semanas',
      custom: 'Elegir una fecha',
      badge: 'Pospuesta hasta {{time}}',
      wake: 'Reactivar ahora',
      snoozedToast: 'Pospuesta hasta {{time}}',
      wokenToast: 'De vuelta en la bandeja',
    },
    detail: {
      problem: 'Problema',
      currentBehavior: 'Qué pasa ahora',
      expectedOutcome: 'Resultado esperado',
      impact: 'Impacto',
      impactUnknown: 'Impacto aún desconocido',
      reproduction: 'Cómo reproducirlo',
      affectedArea: 'Área afectada',
      progress: 'Progreso',
      decisions: 'Historial de decisiones',
      comments: 'Comentarios',
      linkedWork: 'Trabajo vinculado',
      linkedWorkEmpty: 'Todavía no hay trabajo vinculado.',
      required: 'Requerido',
      optional: 'Opcional',
      hiddenWork: 'Parte del trabajo vinculado es restringido y no se muestra.',
      pendingQuestion: 'El equipo preguntó',
      answerPlaceholder: 'Responde la pregunta…',
      duplicateOf: 'Duplicada de',
      decisionReason: 'Nota de la decisión',
      source: 'Origen',
      triage: 'Triage',
      triageTeam: 'Equipo de triage',
      followers: 'Seguir',
      following: 'Siguiendo',
      linkWork: 'Vincular trabajo',
      linkWorkSearch: 'Busca trabajo por clave o título',
      unlink: 'Desvincular',
    },
    confirm: {
      fromEmail: 'Elige tu respuesta abajo. No se registra nada hasta que hagas clic.',
      title: '¿Esto quedó resuelto para ti?',
      body: 'El equipo marcó esta solicitud como resuelta. Cuéntales si te funciona.',
      confirmed: 'Sí, está resuelto',
      persists: 'No, sigue ocurriendo',
      commentPlaceholder: 'Agrega detalles (opcional)',
      thanks: 'Gracias por confirmar.',
      reported: 'Gracias — el equipo lo revisará de nuevo.',
      generation: 'Entrega n.º {{generation}}',
    },
    dialogs: {
      rejectTitle: 'Rechazar solicitud',
      duplicateTitle: 'Marcar como duplicada',
      duplicateSearch: 'Busca la solicitud principal',
      askTitle: 'Pedir información',
      askPlaceholder: '¿Qué necesitas saber?',
      fulfillTitle: 'Marcar como atendida',
      fulfillDirect: 'Describe el resultado entregado',
      reviewTitle: 'Resolver tras revisión',
      reviewPlaceholder: 'Explica por qué el resultado actual ya atiende la solicitud',
      correctionTitle: 'Aceptar corrección',
      correctionWork: 'Ítem de trabajo correctivo (opcional)',
      cancelTitle: 'Cancelar solicitud',
      reconsiderTitle: 'Reconsiderar solicitud',
    },
  },
  comments: {
    react: 'Agregar reacción',
    summary: { action: 'Resumir la discusión', title: 'Resumen con IA', again: 'Regenerar', note: 'Hecho con IA a partir de los {{count}} comentarios que puedes ver. Puede equivocarse; la fuente es el hilo.' },
    reactedBy: '{{names}} reaccionó con {{reaction}}',
    reactions: { THUMBS_UP: 'pulgar arriba', THUMBS_DOWN: 'pulgar abajo', LAUGH: 'risa', HOORAY: 'celebración', CONFUSED: 'confusión', HEART: 'corazón', ROCKET: 'cohete', EYES: 'ojos' },
    mentionPeople: 'Mencionar a alguien',
    mentionHint: 'Escribe @ para mencionar a alguien; recibirá una notificación.',
  },
  labels: {
    title: 'Etiquetas',
    manage: 'Administrar etiquetas…',
    hint: 'Dale a cada etiqueta un color y una descripción. Las que no tienen se muestran en gris; borrar una solo quita su color, nunca la etiqueta de los elementos de trabajo.',
    name: 'Etiqueta',
    color: 'Color',
    description: 'Descripción (opcional)',
    add: 'Guardar etiqueta',
    saved: 'Etiqueta guardada',
    delete: 'Borrar el color de {{name}}',
    empty: 'Ninguna etiqueta tiene color todavía.',
    colors: { gray: 'Gris', red: 'Rojo', orange: 'Naranja', amber: 'Amarillo', green: 'Verde', teal: 'Verde azulado', blue: 'Azul', indigo: 'Índigo', purple: 'Violeta', pink: 'Rosa' },
  },
  inbox: {
    title: 'Bandeja',
    subtitle: 'Novedades del trabajo y las solicitudes en las que participas.',
    tabs: { inbox: 'Todas', mentions: 'Menciones', unread: 'Sin leer', snoozed: 'Pospuestas', archived: 'Archivadas' },
    empty: { inbox: 'Estás al día.', mentions: 'Nadie te mencionó todavía.', unread: 'Nada sin leer.', snoozed: 'Nada pospuesto.', archived: 'Nada archivado.' },
    markAllRead: 'Marcar todo como leído',
    allRead: 'Todo marcado como leído',
    archiveRead: 'Archivar leídas',
    archivedRead: 'Notificaciones leídas archivadas',
    markRead: 'Marcar como leída',
    archive: 'Archivar',
    unarchive: 'Volver a la bandeja',
    snooze: 'Posponer',
    snoozeFor: { day: 'Hasta mañana', threeDays: 'Por 3 días', week: 'Por una semana' },
    snoozed: 'Pospuesta',
    unsnooze: 'Traer ahora',
    snoozedUntil: 'pospuesta hasta {{time}}',
    unread: 'Sin leer',
    unreadCount_one: '{{count}} sin leer',
    unreadCount_other: '{{count}} sin leer',
    actions: 'Acciones de la notificación',
  },
  templates: {
    use: 'Empezar desde una plantilla…',
    manage: 'Administrar',
    manageWork: 'Plantillas de trabajo',
    manageRequest: 'Plantillas de solicitud',
    saveAs: 'Guardar como plantilla',
    saveTitle: 'Guardar como plantilla',
    saveHintWork: 'Guarda el título, tipo, prioridad, descripción, criterios, etiquetas y subelementos para que el próximo elemento empiece desde ahí.',
    saveHintRequest: 'Guarda el texto de la solicitud para que la próxima empiece desde ahí.',
    name: 'Nombre de la plantilla',
    description: 'Descripción (opcional)',
    onlyThisProject: 'Ofrecerla solo en este proyecto',
    saved: 'Plantilla guardada',
    everywhere: 'Todos los proyectos',
    remove: 'Quitar la plantilla {{name}}',
    empty: 'Todavía no hay plantillas. Guarda una desde un formulario o un elemento de trabajo.',
    subItems_one: 'Crea {{count}} subelemento',
    subItems_other: 'Crea {{count}} subelementos',
  },
  intake: {
    title: 'Nueva solicitud',
    subtitle: 'Describe lo que está pasando. No necesitas saber cómo arreglarlo.',
    tabs: { assistant: 'Guiado', form: 'Formulario' },
    assistantIntro: 'Cuéntame el problema o la idea con tus palabras. Solo te preguntaré lo que falte.',
    assistantPlaceholder: 'Escribe tu respuesta…',
    assistantUnavailable: 'El asistente no está disponible ahora. Usa el formulario; todo se guarda.',
    assistant: {
      ack: 'Gracias, lo guardé en el borrador.',
      askProblem: '¿Qué está pasando y dónde?',
      askExpected: '¿Qué debería pasar en cambio?',
      askImpact: '¿A quién afecta y cuánto importa? Está bien decir que no lo sabés.',
      askReproduction: 'Si es un error: ¿cómo se puede ver que ocurre y con qué frecuencia?',
      ready: 'Ya tengo lo que necesita triage. Revisá el resumen y envialo cuando esté bien.',
      fallback: 'El asistente no está disponible, así que guardé tu respuesta tal cual.',
    },
    fields: {
      title: 'Título corto',
      titlePlaceholder: 'p. ej.: La app no muestra el historial de intercambios',
      problem: '¿Cuál es el problema?',
      problemPlaceholder: 'Describe la situación y a quién afecta',
      currentBehavior: '¿Qué pasa ahora?',
      expectedOutcome: '¿Qué debería pasar?',
      impact: 'Impacto',
      impactPlaceholder: '¿A quién o qué afecta, y cuánto?',
      impactUnknown: 'Todavía no conozco el impacto',
      reproduction: 'Pasos para reproducirlo (para bugs)',
      affectedArea: 'Área o producto afectado',
      language: 'Escrito en',
      visibility: 'Quién puede verlo',
      team: 'Enviar al equipo',
      teamAuto: 'Que decida el triage',
      priority: 'Prioridad',
      evidence: 'Adjuntos',
    },
    duplicates: 'Solicitudes posiblemente relacionadas',
    duplicatesHelp: 'Revisa si alguna ya cubre tu solicitud.',
    review: 'Revisar y enviar',
    reviewHelp: 'Revisa el resumen. Todavía puedes editar antes de enviar.',
    missing: 'Falta: {{fields}}',
    saved: 'Borrador guardado',
    submitted: 'Solicitud {{key}} enviada',
    draftNote: 'Los borradores son privados hasta que los envíes.',
  },
  work: {
    title: 'Trabajo',
    defectCause: {
      title: 'Causa',
      causedBy: 'Causado por',
      causedByHelp: 'El trabajo entregado que introdujo este bug (hasta 5).',
      none: 'Sin causa de entrega',
      noneHelp: 'Ninguna entrega lo causó.',
      category: 'Categoría',
      note: 'Nota',
      notePlaceholder: '¿Qué pasó?',
      reasons: { PRE_EXISTING: 'Preexistente', EXTERNAL: 'Externo o proveedor', CONFIGURATION: 'Configuración', DATA: 'Datos', OTHER: 'Otro' },
      search: 'Buscar el trabajo que lo causó',
      notInformed: 'Causa no informada',
      notInformedHint: 'Indica qué trabajo causó este bug, o que ninguna entrega lo causó.',
      inform: 'Informar causa',
      change: 'Cambiar',
      changeTitle: 'Cambiar causa',
      clear: 'Quitar',
      saved: 'Causa guardada',
      noDeliveryCause: 'Sin causa de entrega: {{reason}}',
    },
    subtitle: 'Todo lo que está en curso entre equipos, proyectos y repositorios.',
    views: { list: 'Lista', board: 'Tablero' },
    empty: 'Ningún trabajo coincide con estos filtros.',
    columns: { key: 'Clave', title: 'Título', phase: 'Fase', priority: 'Prioridad', type: 'Tipo', assignee: 'Responsable', project: 'Proyecto', team: 'Equipo', updated: 'Actualizado', delivery: 'Entrega', labels: 'Etiquetas', created: 'Creado', owner: 'Propietario', start: 'Inicio', due: 'Vence', points: 'Puntos' },
    filters: { phase: 'Fase', type: 'Tipo', priority: 'Prioridad', assignee: 'Responsable', project: 'Proyecto', team: 'Equipo', blocked: 'Solo bloqueados', provisional: 'Solo provisionales', includeCanceled: 'Incluir cancelados', label: 'Etiqueta', archived: 'Solo archivados', due: 'Vencimiento', dueOverdue: 'Vencidos', dueSoon: 'Vencen en los próximos 7 días', dueNone: 'Sin fecha de vencimiento' },
    bulk: { selected: '{{count}} seleccionados', setPriority: 'Definir prioridad', assign: 'Asignar', addLabel: 'Agregar etiqueta', clear: 'Limpiar selección', setProject: 'Mover a proyecto', noProject: 'Sin proyecto', setTeam: 'Definir equipo', removeLabel: 'Quitar etiqueta', labelPlaceholder: 'Etiqueta', archive: 'Archivar', restore: 'Restaurar', setDue: 'Definir vencimiento', clearDue: 'Quitar vencimiento', cancel: 'Cancelar trabajo', cancelTitle: 'Cancelar {{count}} ítems de trabajo', cancelHint: 'Todos los ítems seleccionados se cancelan con este motivo.' },
    columnsMenu: { title: 'Columnas', reset: 'Mostrar columnas predeterminadas' },
    export: { title: 'Exportar', csv: 'Archivo CSV', xlsx: 'Archivo de Excel (.xlsx)', hint: 'Descarga los filtros actuales, hasta 5000 filas.' },
    groupBy: 'Agrupar',
    groupNone: 'Sin agrupar',
    lanes: 'Filas',
    lanesNone: 'Sin filas',
    closedWindow: { label: 'Cerradas', days: 'Cerradas: últimos {{count}} días', all: 'Cerradas: todas' },
    groupFields: { phase: 'Fase', priority: 'Prioridad', assignee: 'Responsable', project: 'Proyecto', team: 'Equipo', type: 'Tipo', label: 'Etiqueta' },
    groups: { noProject: 'Sin proyecto', noTeam: 'Sin equipo', noLabel: 'Sin etiqueta' },
    board: { moveTo: 'Mover a…', dropRejected: 'Ese movimiento no está permitido: {{reason}}', productionHint: 'El trabajo de producción solo queda Terminado con despliegues verificados.' },
    create: {
      title: 'Nuevo ítem de trabajo',
      responsibility: 'Responsable',
      team: 'Equipo',
      personal: 'Personal (yo)',
      project: 'Proyecto',
      noProject: 'Sin proyecto',
      type: 'Tipo',
      policy: 'Finalización',
      description: 'Descripción',
      criteria: 'Criterios de aceptación',
      addCriterion: 'Agregar criterio',
      criterionPlaceholder: 'Resultado observable…',
      assignee: 'Responsable',
      labels: 'Etiquetas',
      labelsPlaceholder: 'separadas, por comas',
      startDate: 'Fecha de inicio',
      dueDate: 'Fecha de vencimiento',
      created: '{{key}} creado',
    },
    provisional: {
      confirm: 'Confirmar como trabajo nuevo',
      merge: 'Fusionar con trabajo existente',
      mergeTitle: 'Fusionar con trabajo existente',
      mergeHint: 'La clave pasa a ser un alias del ítem elegido. Contribuciones, sesiones, pull requests y vínculos con solicitudes se trasladan; no se borra nada.',
      mergeInto: 'Fusionar con {{key}}',
      confirmed: 'Confirmado como trabajo nuevo',
      merged: 'Fusionado con {{key}}',
    },
    detail: {
      overview: 'Resumen',
      activity: 'Actividad',
      progress: 'Progreso',
      delivery: 'Entrega',
      code: 'Código',
      codeTab: {
        pullRequests: 'Pull requests',
        commits: 'Commits',
        evidence: 'Links reportados por agentes',
        noPullRequests: 'No hay pull requests vinculados.',
        noCommits: 'No hay commits vinculados.',
        linkLabel: 'Vincular un commit o pull request',
        linkHint: 'Pega su URL de GitHub. Los commits y pull requests que mencionan {{key}} se vinculan solos.',
        link: 'Vincular',
        linkedCommit: 'Commit vinculado',
        linkedPr: 'Pull request vinculado',
        unlink: 'Desvincular',
        unlinked: 'Vínculo quitado',
        viaKey: 'Por clave',
        viaVammoApps: 'Desde Vammo Apps',
        manual: 'Vinculado a mano',
        prState: {
          OPEN: 'Abierto',
          CLOSED: 'Cerrado',
          MERGED: 'Mergeado',
        },
        errors: {
          not_connected: 'Ese repositorio no está conectado. Instala primero la GitHub App en él.',
          not_synced: 'Ese pull request todavía no se sincronizó. Asocia el repositorio a un proyecto y vuelve a intentar en unos minutos.',
          not_found: 'No existe un commit con ese SHA en el repositorio.',
          unsupported: 'Pega la URL de un commit o pull request de GitHub.',
          invalid_format: 'Pega la URL de un commit o pull request de GitHub.',
        },
      },
      history: 'Historial',
      description: 'Descripción',
      noDescription: 'Todavía sin descripción.',
      criteria: 'Criterios de aceptación',
      noCriteria: 'Sin criterios de aceptación.',
      properties: 'Propiedades',
      owner: 'Dueño',
      reviewer: 'Revisor',
      repositories: 'Repositorios',
      noRepositories: 'Sin repositorios — está bien para trabajo sin código.',
      labels: 'Etiquetas',
      blockers: 'Bloqueos',
      addBlocker: 'Agregar bloqueo',
      blockerReason: '¿Qué lo está bloqueando?',
      preventsCompletion: 'Impide terminar',
      resolveBlocker: 'Resolver',
      relations: 'Trabajo relacionado',
      addRelation: 'Vincular trabajo',
      relationKinds: { PARENT_OF: 'Padre de', DEPENDS_ON: 'Depende de', RELATES_TO: 'Relacionado con', DUPLICATES: 'Duplica', CAUSED_BY: 'Causado por' },
      defect: 'Defecto',
      requiresCompletion: 'Requerido para terminar',
      contributors: 'Contribuidores',
      linkedRequests: 'Solicitudes',
      outcome: 'Resultado',
      outcomePlaceholder: '¿Qué se entregó?',
      complete: 'Completar entregable',
      criterionStatus: { OPEN: 'Abierto', MET: 'Cumplido', NOT_MET: 'No cumplido', WAIVED: 'Dispensado' },
      episodes: 'Episodios de finalización',
      provisionalNote: 'Creado de forma provisional por un agente; confírmalo o fusiónalo.',
      blockedNote: 'Bloqueado',
      awaitingDeployment: 'Esperando verificación del despliegue',
      sessions: 'Sesiones de agentes',
      evidence: 'Evidencia',
      noEvidence: 'No hay evidencia adjunta.',
      archivedBanner: 'Archivado {{time}}. Restáuralo para hacer cambios.',
      archive: 'Archivar',
      unarchive: 'Restaurar',
      archivedToast: 'Elemento de trabajo archivado',
      restoredToast: 'Elemento de trabajo restaurado',
      delete: 'Eliminar definitivamente',
      deleteTitle: '¿Eliminar {{key}} definitivamente?',
      deleteHint: 'Se elimina el elemento de trabajo con sus comentarios, adjuntos, traducciones y progreso. El registro de auditoría conserva una constancia con tu motivo. No se puede deshacer.',
      deleteConfirm: 'Escribe {{key}} para confirmar',
      deletedToast: '{{key}} eliminado',
      addSubItem: 'Agregar subelemento',
      subItemOf: 'Subelemento de {{key}}',
      copyBranch: 'Copiar nombre de rama',
      branchCopied: 'Nombre de rama copiado: {{branch}}',
      copyKey: 'Copiar clave',
      keyCopied: '{{key}} copiada',
      editLabels: 'Editar etiquetas',
      labelsPlaceholder: 'Escribe una etiqueta y presiona Enter',
      noLabels: 'Sin etiquetas',
      shortcuts: { title: 'Atajos de teclado', priority: 'Cambiar prioridad', assign: 'Cambiar responsable', assignMe: 'Asignármelo', labels: 'Editar etiquetas', copyKey: 'Copiar clave' },
      startDate: 'Fecha de inicio',
      dueDate: 'Fecha de vencimiento',
      overdue: 'Vencido',
      dueToday: 'Vence hoy',
      clearDate: 'Quitar fecha',
      subItems: 'Subelementos',
      noSubItems: 'Todavía sin subelementos.',
      unlinkSubItem: 'Quitar de los subelementos',
      parents: 'Elementos padre',
      progressOf: '{{done}} de {{total}} terminados',
      timelineEmpty: 'Todavía sin historial.',
    },
  },
  projects: {
    slack: {
      title: 'Novedades en Slack',
      hint: 'Elige el canal de Slack donde se publican las novedades de este proyecto, y cuáles. Pueden cambiarlo los responsables del proyecto y los administradores.',
      notConnected: 'Slack no está conectado en este entorno.',
      listFailed: 'Slack no devolvió sus canales en este momento; vuelve a intentarlo en un minuto.',
      channel: 'Canal',
      none: 'Ningún canal',
      private: 'privado',
      notMember: 'La app todavía no está en #{{channel}}. Invítala en Slack con /invite @Vammo Work Tracker; si no, las novedades no se publican.',
      replaces_one: 'Un administrador configuró {{count}} canal; al guardar queda solo el que elijas aquí.',
      replaces_other: 'Un administrador configuró {{count}} canales; al guardar queda solo el que elijas aquí.',
      workEvents: 'Tareas',
      requestEvents: 'Solicitudes',
      save: 'Guardar canal de Slack',
      saved: 'Canal de Slack guardado',
      removed: 'El proyecto ya no publica en Slack',
    },
    vammoApps: {
      tabs: { STANDARD: 'Proyectos', VAMMO_APP: 'Vammo Apps' },
      badge: 'Vammo App',
      open: 'Abrir en Vammo Apps',
      hint: 'Un proyecto por cada app de la plataforma Vammo Apps. Cada pedido de deploy es un trabajo que se completa solo cuando corre en producción.',
      empty: 'Todavía no hay Vammo Apps.',
      managed: 'Sincronizado con la plataforma Vammo Apps: los trabajos siguen los pedidos de deploy y los deploys de esta app.',
    },
    activity: {
      subtitle: 'Trabajo, entregas y participación en cada proyecto.',
      search: 'Buscar proyectos por nombre o clave…',
      includeArchived: 'Incluir archivados',
      noResults: 'No hay proyectos que coincidan con la búsqueda.',
      loaded: '{{count}} proyectos cargados',
      weekHint: 'Esta semana · desde el lunes · {{timezone}}',
      scopeHint:
        'Los conteos respetan tus permisos. Los commits y deploys exitosos a producción corresponden a repositorios vinculados y pueden repetirse entre proyectos. Se muestra la actividad registrada; los datos faltantes no se cuentan como cero.',
      openProject: 'Abrir {{name}}',
      archived: 'Archivado',
      repositoryCount_one: "{{count}} repositorio",
      repositoryCount_other: "{{count}} repositorios",
      lastActivity: 'Última actividad',
      noActivity: 'Sin actividad registrada todavía',
      open: 'Tareas abiertas',
      inProgress: '{{count}} en curso',
      blocked_one: "{{count}} bloqueada",
      blocked_other: "{{count}} bloqueadas",
      openHint:
        'Tareas actuales, sin archivar, que podés ver. Excluye finalizadas, canceladas y expiradas. Cada tarea bloqueada se cuenta una vez.',
      completed: 'Finalizadas',
      thisWeek: 'Esta semana',
      imported_one: "+ {{count}} importada",
      imported_other: "+ {{count}} importadas",
      completedHint:
        'Tareas distintas con una finalización esta semana, incluso si luego se reabrieron. Las finalizaciones repetidas se cuentan una vez. Excluye canceladas, expiradas, archivadas y provisionales. Las importadas aparecen por separado; finalizar no implica necesariamente una entrega verificada a producción.',
      commits: 'Commits',
      deployments: 'Deploys',
      commitsHint:
        'Commits registrados en los repositorios vinculados al proyecto, según su fecha original. Los repositorios compartidos pueden aparecer en varios proyectos. La sincronización no garantiza un historial completo.',
      deploymentsHint:
        'Deploys exitosos a producción de servicios asociados a los repositorios del proyecto, según su fecha de finalización. Excluye intentos fallidos, rollbacks y deploys sin fecha de finalización. Es actividad del servicio; no prueba que se hayan entregado todas las tareas del proyecto.',
      activePeople: 'Personas activas',
      peopleHint:
        'Cuentas activas distintas que modificaron tareas visibles, comentaron en ellas o actualizaron el proyecto esta semana. Incluye agentes actuando por una persona verificada. No cuenta importaciones históricas, eventos del sistema ni personas solamente asignadas. Es actividad, no presencia en línea ni una medida de productividad.',
      lastSync: 'Última sincronización exitosa: {{date}}.',
      coverage: {
        PARTIAL: 'Registrados · parcial',
        FIXTURE: 'Datos de prueba',
        UNAVAILABLE: 'Sin datos sincronizados',
        UNMAPPED: 'Sin vinculación',
        FORBIDDEN: 'Restringido',
      },
    },
    automation: {
      title: 'Reglas automáticas',
      hint: 'Se aplican cada hora al trabajo de este proyecto. Deja un campo vacío para apagar esa regla.',
      staleAfter: 'Sin actividad por (días)',
      staleAction: 'Entonces',
      actions: { NOTIFY: 'Avisar al responsable', CANCEL: 'Cancelarlo (lo que pide un request solo recibe aviso)' },
      autoArchive: 'Archivar lo terminado después de (días)',
      autoArchiveHint: 'Lo terminado o cancelado sale de las listas; se sigue encontrando en Archivados.',
      off: 'Apagada',
    },
    updates: {
      title: 'Estado del proyecto',
      statusLabel: 'Estado del proyecto',
      status: { ON_TRACK: 'En camino', AT_RISK: 'En riesgo', OFF_TRACK: 'Desviado' },
      placeholder: '¿Qué cambió, qué está en riesgo, qué sigue?',
      post: 'Publicar estado',
      posted: 'Estado publicado',
      empty: 'Todavía no hay actualizaciones de estado.',
      edited: 'editado',
      reply: 'Responder',
      replyPlaceholder: 'Escribe una respuesta…',
    },
    addManager: 'Agregar responsable',
    addTeam: 'Agregar equipo responsable',
    archive: 'Archivar este proyecto',
    archiveHint: 'Los proyectos archivados salen de listas y selectores; su trabajo e historial siguen visibles.',
    title: 'Proyectos',
    subtitle: 'Los proyectos agrupan trabajo; los repositorios son opcionales.',
    new: 'Nuevo proyecto',
    empty: 'Todavía no hay proyectos.',
    key: 'Clave',
    name: 'Nombre',
    description: 'Descripción',
    managers: 'Responsables',
    owningTeams: 'Equipos dueños',
    openWork: '{{count}} abiertos',
    defaultPolicy: 'Finalización por defecto',
    requiresReview: 'Exigir revisión independiente para entregables',
    settings: 'Configuración',
    visibility: 'Visibilidad',
    repositories: 'Repositorios',
  },
  teams: {
    title: 'Equipos',
    members: 'Miembros',
    manager: 'Responsable',
    member: 'Miembro',
    addMember: 'Agregar miembro',
    remove: 'Quitar',
    new: 'Nuevo equipo',
    slug: 'Identificador',
    archive: 'Archivar equipo',
    restore: 'Restaurar equipo',
    archived: 'Equipo archivado',
    restored: 'Equipo restaurado',
    archivedBadge: 'Archivado',
    archiveHint: 'Oculta el equipo de listas y selectores. Primero mové sus proyectos, trabajo abierto y solicitudes abiertas a otro equipo.',
  },
  documents: {
    title: 'Documentos',
    subtitle: 'Documentación de la empresa en carpetas, con quién ve y quién edita definido por carpeta.',
    folders: 'Carpetas',
    moreActions: 'Más acciones',
    archiveLink: 'Archivados',
    archiveTitle: 'Documentos y carpetas archivados',
    archiveHint: 'Lo archivado conserva su contenido e historial. Lo restauran los editores de su carpeta.',
    archiveEmpty: 'No hay nada archivado.',
    restore: 'Restaurar',
    restoredItem: 'Restaurado',
    resizeTree: 'Cambiar el ancho del panel de carpetas',
    moveNotAllowed: 'No puedes moverlo ahí',
    dropToRoot: 'Suelta aquí para dejarla en el primer nivel',
    open: 'Abrir',
    copyLink: 'Copiar link',
    linkCopied: 'Link copiado',
    changeIcon: 'Cambiar ícono',
    removeIcon: 'Quitar ícono',
    iconSaved: 'Ícono actualizado',
    permissions: 'Permisos',
    renameDocument: 'Renombrar documento',
    documentTitle: 'Título del documento',
    export: 'Exportar',
    exportPdf: 'PDF (imprimir o guardar)',
    exportMarkdown: 'Markdown (.md)',
    exportHtml: 'Página web (.html)',
    exportWord: 'Word (.docx)',
    exporting: 'Preparando la exportación…',
    linkTitle: 'Link',
    linkTabUrl: 'Dirección web',
    linkTabDocument: 'Documento',
    linkTabTask: 'Tarea',
    linkUrl: 'Dirección',
    searchDocuments: 'Busca documentos por título',
    searchTasks: 'Busca tareas por título o clave',
    removeLink: 'Quitar link',
    applyLink: 'Aplicar',
    noMatches: 'Sin resultados',
    mentionInvite: 'Invitar a {{email}}',
    mentionEmpty: 'Escribe un nombre, o un email de la empresa para invitar a alguien',
    mentionInvited: '{{name}} verá la mención cuando inicie sesión por primera vez',
    insertMedia: 'Imagen o video',
    uploadingMedia: 'Subiendo {{name}}…',
    mediaFailed: 'No se pudo subir {{name}}',
    mediaType: 'En los documentos solo van imágenes (PNG, JPEG, GIF, WebP) y videos (MP4, WebM, MOV)',
    comments: 'Comentarios',
    comment: 'Comentar',
    addComment: 'Agregar comentario',
    commentPlaceholder: 'Escribe un comentario. Usa @ para mencionar a alguien.',
    reply: 'Responder',
    replyPlaceholder: 'Responder…',
    resolve: 'Resolver',
    reopen: 'Reabrir',
    resolvedBy: 'Resuelto por {{name}}',
    showResolved: 'Mostrar resueltos',
    detached: 'El texto comentado cambió o se borró',
    noComments: 'Selecciona texto del documento para comentarlo.',
    editComment: 'Editar',
    deleteComment: 'Borrar',
    deleteCommentConfirm: '¿Borrar este comentario?',
    edited: 'editado',
    expandFolder: 'Expandir {{name}}',
    collapseFolder: 'Contraer {{name}}',
    newFolder: 'Nueva carpeta',
    newSubfolder: 'Nueva subcarpeta',
    newDocument: 'Nuevo documento',
    untitled: 'Sin título',
    folderName: 'Nombre de la carpeta',
    rename: 'Renombrar',
    renamed: 'Renombrado',
    archiveFolder: 'Archivar carpeta',
    archiveFolderHint: 'Solo se archivan carpetas vacías. Primero archiva o mueve sus documentos y subcarpetas.',
    archiveDocument: 'Archivar documento',
    restoreDocument: 'Restaurar documento',
    archived: 'Archivado',
    archivedNotice: 'Este documento está archivado. Restáuralo para volver a editarlo.',
    emptyTree: 'Todavía no hay carpetas',
    emptyTreeHint: 'Crea una carpeta para empezar a ordenar documentos. Quedas como su primer editor.',
    emptyFolder: 'Esta carpeta está vacía.',
    subfolders: 'Subcarpetas',
    documentsHeading: 'Documentos',
    pickFolder: 'Elige una carpeta o un documento a la izquierda.',
    documentCount_one: '{{count}} documento',
    documentCount_other: '{{count}} documentos',
    restricted: 'Restringida',
    company: 'Toda la empresa',
    canEdit: 'Puedes editar',
    readOnly: 'Solo lectura',
    updatedBy: 'Editado por {{name}}',
    access: 'Acceso',
    accessTitle: 'Quién ve y quién edita',
    viewers: 'Quién puede ver',
    viewersCompany: 'Toda la empresa',
    viewersCompanyHelp: 'Todos los que ven la carpeta de arriba (en una carpeta de primer nivel, cualquier empleado de Vammo con sesión iniciada).',
    viewersRestricted: 'Solo las personas, equipos, grupos y proyectos de abajo',
    viewersRestrictedHelp: 'Una subcarpeta solo puede restringir la audiencia de su carpeta de arriba. Los editores de esta lista también ven. Los Superadmins siempre ven todo.',
    editors: 'Quién puede editar',
    editInherit: 'Igual que la carpeta de arriba',
    editViewers: 'Todos los que pueden ver',
    editListed: 'Solo las personas, equipos, grupos y proyectos de abajo',
    editsFrom: 'Los editores vienen de “{{folder}}”.',
    everyoneWhoViews: 'Todos los que ven pueden editar.',
    addPrincipal: 'Agregar personas, equipos, grupos o proyectos',
    removePrincipal: 'Quitar a {{name}}',
    accessSaved: 'Acceso actualizado',
    principalKind: { USER: 'Persona', TEAM: 'Equipo', GROUP: 'Grupo', PROJECT: 'Proyecto' },
    saving: 'Guardando…',
    saved: 'Guardado',
    unsaved: 'Cambios sin guardar',
    saveFailed: 'No se pudo guardar. Tus cambios siguen aquí; reintentando…',
    conflictTitle: 'Otra persona guardó este documento',
    conflictBody: 'Tus últimos cambios no se guardaron porque el documento cambió. Copia lo que quieras conservar y recarga para ver la última versión.',
    reload: 'Recargar',
    alsoEditing: 'También editando: {{names}}',
    placeholder: 'Escribe aquí. Usa la barra de herramientas o atajos de Markdown como # o -.',
    titlePlaceholder: 'Título',
    bodyLabel: 'Contenido del documento',
    lock: 'Hacer borrador privado',
    lockEdit: 'Ajustes del borrador privado',
    unlock: 'Quitar candado',
    locked: 'Borrador privado',
    lockHelp: 'Un borrador privado solo lo ven tú y las personas que nombres. Aun así necesitan acceso a la carpeta. Al quitar el candado, el documento vuelve a tener los permisos de la carpeta.',
    lockPeople: 'Personas que también ven el borrador',
    lockAddPerson: 'Agregar una persona',
    lockWarning: 'Estas personas no ven la carpeta, así que todavía no pueden abrir el borrador: {{names}}',
    lockSaved: 'Ajustes del borrador guardados',
    unlocked: 'El documento ahora tiene los permisos de la carpeta',
    history: 'Historial',
    historyEmpty: 'Todavía no hay revisiones.',
    revisionN: 'Revisión {{n}}',
    revisionKind: { CREATE: 'Creado', EDIT: 'Editado', RESTORE: 'Restaurado' },
    restoredFrom: 'Restaurado desde la revisión {{n}}',
    viaAgent: 'vía {{client}}',
    restoreRevision: 'Restaurar esta versión',
    restoreConfirm: '¿Restaurar la revisión {{n}}? Se guarda como una revisión nueva, así que no se pierde nada.',
    restored: 'Revisión restaurada',
    adminAccesses: 'Accesos de Superadmins',
    adminAccessesHelp: 'Superadmins que abrieron este documento sin ser parte de su audiencia.',
    move: 'Mover',
    moveTo: 'Mover a la carpeta',
    moved: 'Movido',
    topLevel: 'Primer nivel',
    chipRestricted: 'Documento restringido',
    commentOnPage: 'Comentar el documento',
    pageComment: 'Sobre todo el documento',
    colors: {
      label: 'Colores',
      none: 'Color predeterminado',
      callout: 'Color del destacado',
      gray: 'Gris',
      red: 'Rojo',
      orange: 'Naranja',
      yellow: 'Amarillo',
      green: 'Verde',
      blue: 'Azul',
      purple: 'Violeta',
      pink: 'Rosa',
    },
    files: {
      attach: 'Adjuntar un archivo',
      download: 'Descargar {{name}}',
    },
    workChip: {
      insert: 'Tarea',
      hint: 'Buscá una tarea por clave o título. También se enlaza escribiendo una clave como VAM-123 seguida de un espacio.',
      unavailable: 'Esta tarea no existe o no tenés acceso',
    },
    presence: {
      label: 'Personas en este documento ahora',
      editing: '{{name}} está editando',
      viewing: '{{name}} está leyendo',
    },
    collab: { rejected: 'Un cambio no se pudo guardar, así que el documento se recargó con la última versión de todos.' },
    backlinks: { title: 'Documentos', mentionedIn: 'Mencionado en' },
    projectFolder: { title: 'Documentación', choose: 'Elegir la carpeta de documentación', hint: 'La carpeta donde este proyecto guarda sus documentos. Quien puede leerla la ve acá.', folder: 'Carpeta', none: 'Todavía no hay carpeta de documentación.', noneOption: 'Sin carpeta' },
    person: {
      tasks: 'Tareas',
      documents: 'Documentos',
      recent: 'Actividad reciente',
      noActivity: 'Sin tareas ni documentos recientes.',
    },
    tableMenu: {
      menu: 'Tabla',
      rowAbove: 'Insertar fila arriba',
      rowBelow: 'Insertar fila abajo',
      columnLeft: 'Insertar columna a la izquierda',
      columnRight: 'Insertar columna a la derecha',
      headerRow: 'Activar o quitar fila de encabezado',
      deleteRow: 'Eliminar fila',
      deleteColumn: 'Eliminar columna',
      deleteTable: 'Eliminar tabla',
    },
    blocks: {
      plainText: 'Texto plano',
      diagramView: 'Diagrama o código',
      code: 'Código',
      add: 'Agregar un bloque',
      basic: 'Básicos',
      richBlocks: 'Bloques',
      mediaAndLinks: 'Archivos y enlaces',
      text: 'Texto',
      insert: 'Insertar',
      callout: 'Destacado',
      toggle: 'Desplegable',
      toc: 'Índice',
      tocEmpty: 'Agregá títulos para armar el índice.',
      video: 'Video',
      videoHint: 'Pegá un enlace de YouTube, Loom, Vimeo o Google Drive.',
      videoUrl: 'Enlace del video',
      insertVideo: 'Insertar video',
      embedUnsupported: 'Solo se pueden insertar videos de YouTube, Loom, Vimeo y Google Drive.',
      mermaid: 'Diagrama Mermaid',
      diagram: 'Diagrama',
      mermaidError: 'El diagrama tiene un error de sintaxis.',
      language: 'Lenguaje del código',
      showCode: 'Ver código',
      hideCode: 'Ocultar código',
    },
    properties: {
      title: 'Propiedades',
      manage: 'Gestionar propiedades',
    },
    import: {
      action: 'Importar',
      menu: 'Importar documentos',
      title: 'Importar en {{folder}}',
      intro: 'Trae documentos de Word, Google Docs, Markdown o Notion a esta carpeta. Cada archivo se convierte en un documento; las imágenes y el archivo original se guardan con él.',
      formats: 'Se aceptan: Word (.docx), páginas web (.html), Markdown (.md), tablas CSV (.csv) y exportaciones de Notion (.zip).',
      notionHelp: 'Notion: en Configuración, elige Exportar, selecciona “Markdown & CSV” con subpáginas y sube el .zip. Sus carpetas se vuelven carpetas, las páginas documentos con sus subpáginas, las bases de datos tablas y las imágenes se traen aquí.',
      googleHelp: 'Google Docs: elige Google Drive abajo para importar directamente desde tu Drive, o usa Archivo → Descargar → Microsoft Word (.docx) o Página web (.html, comprimido) y sube ese archivo.',
      drive: {
        button: 'Google Drive',
        hint: 'Elige documentos de Google Docs, o archivos Word, HTML, Markdown y CSV guardados en Drive. Google te pide una sola vez que permitas a Vammo Work abrir solo los archivos que elijas.',
        notConfigured: 'La importación directa desde Google Drive no está configurada. Pide a un administrador que configure el acceso a Google Drive.',
        opening: 'Abriendo Google Drive…',
        waiting: 'Esperando a Google…',
        pickerTitle: 'Elige los archivos a importar',
        source: 'Google Drive',
        remove: 'Quitar {{name}}',
        errors: {
          unavailable: 'No se pudo conectar con Google Drive. Revisa la conexión e inténtalo de nuevo.',
          popupBlocked: 'El navegador bloqueó la ventana de inicio de sesión de Google. Permite las ventanas emergentes para este sitio e inténtalo de nuevo.',
          popupClosed: 'La ventana de inicio de sesión de Google se cerró antes de conceder el acceso.',
          denied: 'No se concedió el acceso a Google Drive.',
          auth: 'El inicio de sesión de Google expiró o fue revocado. Vuelve a elegir los archivos de Google Drive.',
          forbidden: 'Google Drive denegó el acceso a este archivo. Pide a su propietario que lo comparta contigo o que permita descargarlo.',
          notFound: 'No se encontró el archivo en Google Drive. Puede que se haya eliminado o movido.',
          quota: 'Google Drive está limitando las solicitudes en este momento. Espera un minuto e inténtalo de nuevo.',
          tooLarge: 'El archivo es demasiado grande para exportarlo desde Google Drive.',
          unsupported: 'Este tipo de archivo de Google Drive no se puede importar.',
          network: 'Falló la descarga desde Google Drive. Revisa la conexión e inténtalo de nuevo.',
          timeout: 'Google no respondió a tiempo. Inténtalo de nuevo.',
          closed: 'La sesión de Google Drive se cerró. Vuelve a elegir los archivos.',
        },
      },
      privateNote: 'Los documentos importados empiezan como borradores privados que solo ves tú, para que los revises antes de que los vea la carpeta.',
      choose: 'Elegir archivos',
      fileInput: 'Archivos para importar',
      noFiles: 'Todavía no elegiste archivos.',
      start: 'Importar',
      stop: 'Detener',
      progress: '{{done}} de {{total}} listos',
      progressLabel: 'Progreso de la importación',
      status: {
        pending: 'En espera',
        working: 'Importando',
        done: 'Importado',
        failed: 'Falló',
        skipped: 'No importado',
      },
      summary_one: '{{count}} documento importado en {{folder}}.',
      summary_other: '{{count}} documentos importados en {{folder}}.',
      stopped: 'La importación se detuvo. Lo que ya se importó se queda.',
      lockHint: 'Los documentos importados son borradores privados que solo ves tú. Revisa cada uno y luego desbloquéalo desde su menú Más acciones para compartirlo con la carpeta.',
      created: 'Documentos importados',
      foldersCreated: 'Carpetas creadas',
      skippedTitle: 'No importado',
      failedTitle: 'Con error',
      copy: 'Copiar informe',
      copied: 'Informe copiado',
      footer: 'Importado el {{date}} desde',
      notes_one: '{{count}} nota de importación',
      notes_other: '{{count}} notas de importación',
      issues: {
        equation: 'Ecuaciones conservadas como código LaTeX',
        footnote: 'Notas al pie conservadas como texto simple',
        html: 'HTML que el editor no admite; se conservó su texto: {{detail}}',
        remoteImage: 'Imagen de otro sitio conservada como enlace, sin copiarla: {{detail}}',
        unsafeLink: 'Enlace quitado porque su dirección no está permitida: {{detail}}',
        brokenLink: 'Enlace quitado porque su destino no forma parte de la importación: {{detail}}',
        missingFile: 'Archivo no encontrado en la importación: {{detail}}',
        embed: 'Contenido incrustado no admitido, conservado como enlace: {{detail}}',
        converterWarning: 'Nota de la conversión de Word: {{detail}}',
        comments: 'Los comentarios no se importan',
        rowsCapped: 'Tabla recortada a sus primeras filas ({{detail}})',
        fileTooLarge: 'Archivo de más de 25 MB no subido: {{detail}}',
        uploadFailed: 'No se pudo subir el archivo: {{detail}}',
        notInline: 'Formato de imagen que los documentos no muestran, adjuntado como archivo: {{detail}}',
        tooDeep: 'Subido un nivel: las subpáginas se anidan hasta 8 niveles',
        plainText: 'No se pudo mantener el formato, así que el texto se importó como párrafos simples',
        originalTooLarge: 'Original de más de 25 MB no adjuntado: {{detail}}',
        unsupportedBlock: 'Bloque de Notion no compatible, omitido: {{detail}}',
        columns: 'Las columnas quedaron una debajo de otra',
        database: 'Base de datos de Notion importada como tabla estática: {{detail}}',
        pageNotImported: 'Enlace a una página no importada, conservado como enlace a Notion: {{detail}}',
        truncated: 'Página cortada tras sus primeros {{detail}} bloques',
        cellShortened: 'Valores de la base de datos acortados para que entren: {{detail}}',
        cellDropped: 'Valores de la base de datos omitidos porque no encajaban en la propiedad: {{detail}}',
      },
      tabs: { files: 'Archivos', notion: 'Notion' },
      notion: {
        intro: 'Importa páginas directamente de tu espacio de Notion. En la propia pantalla de Notion eliges qué páginas puede leer Vammo Work; solo esas se pueden importar.',
        connect: 'Conectar Notion',
        connectHint: 'Vas a Notion a elegir las páginas que compartes y vuelves aquí.',
        connectedTo: 'Conectado a {{workspace}}',
        sharePages: 'Compartir otras páginas',
        search: 'Buscar páginas de Notion',
        searchPlaceholder: 'Buscar por título',
        empty: 'Todavía no hay páginas compartidas con Vammo Work. Usa “Compartir otras páginas” para elegirlas en Notion.',
        noMatches: 'Ninguna página compartida coincide.',
        loadMore: 'Cargar más',
        database: 'Base de datos',
        pageList: 'Páginas de Notion compartidas',
        selected_one: '{{count}} página seleccionada',
        selected_other: '{{count}} páginas seleccionadas',
        subpagesNote: 'Las subpáginas vienen incluidas. Las bases de datos se convierten en tablas de hasta 500 filas. Los archivos e imágenes de Notion se copian aquí.',
        start: 'Importar desde Notion',
        queued: 'Esperando que empiece la importación…',
        running: 'Importando desde Notion: {{done}} de {{total}} páginas',
        failedRun: 'La importación se detuvo: {{error}}',
        alreadyRunning: 'Ya tienes una importación de Notion en curso. Espera a que termine o detenla y luego inicia otra.',
        reconnectNeeded: 'Notion ya no acepta tu conexión. Vuelve a conectar Notion.',
        openInNotion: 'Abrir en Notion',
        connected: 'Notion conectado',
        denied: 'No se concedió acceso a Notion',
        stateError: 'No se pudo confirmar la conexión con Notion. Inténtalo de nuevo.',
        connectFailed: 'No se pudo conectar Notion. Inténtalo de nuevo.',
        skipReasons: {
          limit: 'más de 200 páginas en una importación',
          notShared: 'no compartida con Vammo Work',
          duplicate: 'ya importada en esta ejecución',
          cancelled: 'la importación se detuvo antes',
          timeLimit: 'la importación alcanzó antes su tiempo máximo; vuelve a importarla',
          interrupted: 'la importación se interrumpió antes; vuelve a importarla',
        },
      },
      skipReasons: {
        unsafePath: 'ruta insegura o repetida en el zip',
        unreferenced: 'ninguna página lo usa',
        unsupportedType: 'tipo de archivo no admitido',
        tooLarge: 'demasiado grande',
        emptyFile: 'archivo vacío',
        cancelled: 'la importación se detuvo antes',
      },
      errors: {
        zipTooLarge: 'El zip tiene más de {{limit}} MB descomprimido.',
        zipTooManyFiles: 'El zip tiene más de {{limit}} archivos.',
        zipUnreadable: 'El archivo no es un zip legible.',
        nothingToImport: 'El zip no tiene páginas Markdown, HTML ni CSV.',
        cancelled: 'Se detuvo antes de guardar su contenido; el documento está vacío.',
      },
    },
    subpages: {
      title: 'Subpáginas',
      new: 'Nueva subpágina',
      empty: 'Todavía no hay subpáginas.',
    },
    readers: {
      title: 'Quién lo vio',
      hint: 'Personas que abrieron este documento y cuándo lo hicieron por última vez.',
      empty: 'Nadie más lo abrió todavía.',
      views_one: '{{count}} vista',
      views_other: '{{count}} vistas',
    },
    diff: {
      title: 'Comparar versiones',
      content: 'Contenido',
      changes: 'Cambios',
      against: 'Cambios desde la revisión {{n}}',
      first: 'Primera revisión: todo es nuevo',
      noChanges: 'Esta revisión no cambia el texto.',
    },
    translation: {
      label: 'Idioma',
      original: 'Original',
      locales: { en: 'EN', 'pt-BR': 'PT', es: 'ES' },
      reasons: {
        DISABLED: 'Este entorno no tiene traductor automático.',
        DAILY_LIMIT: 'Se alcanzó el límite diario de IA; los bloques sin traducir muestran el original.',
        RATE_LIMIT: 'Demasiados pedidos de traducción; los bloques nuevos se traducen en un minuto.',
        INVALID: 'No se pudo mostrar la traducción.',
      },
      pending: 'Traducción automática en curso: {{done}} de {{total}} bloques listos.',
      ready: 'Traducción automática, solo lectura. El original sigue siendo la fuente.',
      unavailable: 'La traducción automática no está disponible; se muestra el original.',
    },
    draft: {
      start: 'Editar sin publicar',
      banner: 'Cambios sin publicar',
      bannerHint: 'Solo vos ves estos cambios hasta que los publiques.',
      stale: 'El documento cambió después de que empezaste este borrador; publicarlo reemplaza esos cambios.',
      publish: 'Publicar',
      discard: 'Descartar cambios',
      published: 'Cambios publicados',
      staleConfirm: 'El documento cambió después de que empezaste este borrador. ¿Publicar igual y reemplazar esos cambios?',
      discardConfirm: '¿Descartar tus cambios sin publicar?',
    },
    table: {
      byPerson: 'De {{name}}',
      clearPerson: 'Ver los documentos de todos',
      title: 'Tabla de documentos',
      search: 'Buscar por título',
      folder: 'Carpeta',
      allFolders: 'Todas las carpetas',
      addFilter: 'Agregar filtro',
      removeFilter: 'Quitar filtro',
      property: 'Propiedad',
      operator: 'Condición',
      ops: { eq: 'es', contains: 'contiene', empty: 'está vacío', set: 'tiene valor' },
      choose: 'Elegir…',
      yes: 'Sí',
      no: 'No',
      document: 'Documento',
      updated: 'Actualizado',
      empty: 'Ningún documento cumple estos filtros.',
    },
    toolbar: {
      label: 'Formato',
      underline: 'Subrayado',
      textColor: 'Color del texto',
      bold: 'Negrita',
      italic: 'Cursiva',
      strike: 'Tachado',
      code: 'Código en línea',
      heading1: 'Título 1',
      heading2: 'Título 2',
      heading3: 'Título 3',
      bulletList: 'Lista con viñetas',
      orderedList: 'Lista numerada',
      taskList: 'Lista de tareas',
      quote: 'Cita',
      codeBlock: 'Bloque de código',
      link: 'Enlace',
      linkPrompt: 'Dirección del enlace (https://, mailto: o una /ruta)',
      linkInvalid: 'Esa dirección no está permitida',
      table: 'Insertar tabla',
      divider: 'Separador',
      undo: 'Deshacer',
      redo: 'Rehacer',
    },
    public: {
      folderName: 'Externo público',
      folderHint: 'Cualquiera con el enlace público de un documento puede leerlo sin iniciar sesión. Los documentos nuevos empiezan como borradores privados: desbloquea uno para publicarlo.',
      badge: 'Público',
      badgeHint: 'Cualquiera con el enlace público puede leer este documento',
      button: 'Enlace público',
      title: 'Enlace público',
      description: 'Cualquiera con este enlace puede leer el documento sin iniciar sesión, también fuera de Vammo. Los comentarios, el historial y los autores siguen siendo privados.',
      disabled: 'Los enlaces públicos están desactivados en este entorno.',
      notPublicLocked: 'Este documento es un borrador privado, así que no es público. Desbloquéalo para publicarlo.',
      notPublicArchived: 'Este documento está archivado, así que su enlace público no funciona.',
      notPublicOutside: 'Solo los documentos de la carpeta Externo público tienen enlace público.',
      copy: 'Copiar enlace',
      copied: 'Enlace público copiado',
      open: 'Abrir la página pública',
      rotate: 'Cambiar enlace',
      rotateConfirm: 'El enlace actual dejará de funcionar en el acto.',
      rotated: 'Enlace cambiado; el anterior ya no funciona',
      passwordOn: 'Los lectores necesitan una contraseña',
      passwordOff: 'Sin contraseña: basta con el enlace',
      passwordNew: 'Contraseña',
      passwordChange: 'Cambiar contraseña',
      passwordSet: 'Pedir contraseña',
      passwordRemove: 'Quitar contraseña',
      passwordHint: 'Al menos 8 caracteres. Compártela aparte del enlace; no se puede volver a mostrar.',
      passwordSaved: 'Contraseña guardada',
      passwordRemoved: 'Contraseña quitada',
      views: 'Visitas en los últimos 30 días: {{count}}',
      viewsTotal: 'en total: {{count}}',
      confirmTitle: '¿Hacerlo público?',
      confirmBody: 'Cualquiera con el enlace podrá leerlo sin iniciar sesión, incluidas personas fuera de Vammo. Los comentarios, el historial y los autores siguen siendo privados.',
      published: 'Publicado: cualquiera con el enlace público puede leerlo',
      confirmAction: 'Publicar',
      restrictedNotAllowed: 'Toda la empresa ve las carpetas de Externo público, porque todo lo que contienen puede publicarse. Para limitar quién ve esta carpeta, sácala primero de Externo público.',
      restrictedMoveRefused: 'Una carpeta que limita quién la ve (o que tiene una subcarpeta así) no puede moverse a Externo público. Primero haz que la vea toda la empresa.',
    },
  },
  publicPage: {
    loading: 'Cargando el documento',
    notFound: 'Este enlace no está disponible',
    notFoundHint: 'El documento no existe o ya no es público. Pide un enlace nuevo a quien lo compartió.',
    passwordTitle: 'Este documento está protegido',
    passwordHint: 'Escribe la contraseña que recibiste con el enlace.',
    password: 'Contraseña',
    open: 'Abrir',
    wrongPassword: 'La contraseña no es correcta',
    tooMany: 'Demasiados intentos. Espera unos minutos y vuelve a intentarlo.',
    updated: 'Actualizado {{date}}',
    internalDocument: 'documento interno',
    databaseNotShown: 'Base de datos no visible públicamente',
    contents: 'Contenido',
    download: 'descargar',
    sharedFrom: 'Compartido desde Vammo Work',
    error: 'No se pudo cargar el documento.',
    retry: 'Reintentar',
  },
  visibility: {
    title: 'Visibilidad',
    grants: 'Personas, equipos y grupos',
    addGrant: 'Agregar acceso',
    searchPrincipal: 'Busca personas, equipos o grupos',
    inherit: 'Las restricciones de los proyectos padre siempre se aplican.',
    changed: 'Visibilidad actualizada',
  },
  settings: {
    title: 'Configuración',
    profile: 'Perfil',
    displayName: 'Nombre visible',
    language: 'Idioma',
    timezone: 'Zona horaria',
    notifications: 'Notificaciones',
    identities: 'Cuentas vinculadas',
    noIdentities: 'No hay cuentas vinculadas.',
    roles: 'Acceso',
    basic: 'Acceso básico',
    notion: {
      title: 'Notion',
      description: 'Importa páginas de tu espacio de Notion a documentos. Tú eliges en Notion qué páginas puede leer Vammo Work.',
      notConnected: 'Notion no está conectado.',
      connect: 'Conectar Notion',
      connectedTo: 'Conectado a {{workspace}} desde el {{date}}',
      disconnect: 'Desconectar',
      confirmDisconnect: '¿Desconectar Notion?',
      disconnected: 'Notion desconectado',
      revokeHint: 'Desconectar borra el acceso guardado aquí. Para quitar Vammo Work también de Notion, abre Configuración → Conexiones en Notion.',
    },
    github: {
      addLabel: 'Agregar una cuenta de GitHub',
      hint: 'Tu usuario de GitHub, por ejemplo una cuenta personal con la que a veces haces commits. Las cuentas que hacen commits con tu email de Vammo se vinculan solas.',
      placeholder: 'usuario',
      add: 'Agregar',
      added: 'Cuenta de GitHub agregada',
      remove: 'Quitar',
      removed: 'Cuenta de GitHub quitada',
      confirmRemove: '¿Quitar esta cuenta?',
      method: { self: 'Agregada por ti', email: 'Verificada por email de commit', admin: 'Vinculada por un administrador', other: 'Vinculada' },
      errors: {
        notFound: 'No existe una cuenta de GitHub con ese usuario.',
        notUser: 'Solo se pueden agregar cuentas personales de GitHub, no organizaciones ni bots.',
        taken: 'Esta cuenta de GitHub ya está vinculada a otra persona. Pídeselo a un administrador.',
        tooMany: 'Ya tienes la cantidad máxima de cuentas de GitHub.',
        unavailable: 'No se pudo contactar a GitHub. Inténtalo de nuevo en un momento.',
      },
    },
  },
  admin: {
    title: 'Administración',
    users: 'Usuarios y roles',
    groups: 'Grupos de visibilidad',
    integrations: 'Integraciones',
    exceptions: 'Excepciones',
    imports: 'Importación de Jira',
    audit: 'Registro de auditoría',
    operations: 'Operaciones',
    roles: 'Roles',
    capabilities: 'Capacidades',
    roleHints: { SUPERADMIN: 'Control total. Lee todos los registros, incluidas carpetas restringidas, borradores privados y reportes sensibles. Solo un Superadmin nombra Superadmins.', ADMIN: 'Gestiona personas, equipos, grupos, conectores, operaciones e importaciones. Lee solo lo que su propio acceso permite.', DEVELOPER: 'Trabajo de ingeniería y reportes técnicos.', PRODUCT: 'Triage y trabajo de producto.' },
    status: 'Estado',
    suspend: 'Suspender',
    reactivate: 'Reactivar',
    lastLogin: 'Último inicio de sesión',
    agents: { column: 'Agentes de IA', none: 'Ninguno', lastSeen: 'Última conexión {{when}}', via: { PLUGIN: 'Plugin', MCP: 'Solo MCP', UNKNOWN: 'Sin confirmar' }, viaHint: { PLUGIN: 'Plugin {{version}} instalado', MCP: 'Servidor MCP configurado sin el plugin', UNKNOWN: 'Se usó antes de que el tracker registrara cómo se conecta; se confirma en su próxima conexión' }, filter: 'Conexión de agentes', filters: { ALL: 'Todos', PLUGIN: 'Plugin instalado', MCP: 'Solo MCP', NONE: 'Sin agente' } },
    changeAccess: 'Cambiar acceso',
    reasonRequired: 'Se registra un motivo en el log de auditoría.',
    reason: { title: 'Registrar un motivo', label: 'Motivo', hint: 'El motivo queda en el registro de auditoría.', confirm: 'Confirmar' },
    integ: { lastSuccess: 'Último éxito', lastAttempt: 'Último intento', lastError: 'Último error', circuit: 'Circuito abierto hasta', cursor: 'Cursor', probe: 'Probar', reconcile: 'Reconciliar', reconcileQueued: 'Reconciliación encolada', liveVerified: 'Verificado en vivo', health: { HEALTHY: 'Saludable', DEGRADED: 'Degradado', FAILING: 'Fallando', DISABLED: 'Desactivado', UNVERIFIED: 'Sin verificar' }, modes: { live: 'En vivo', fixture: 'Fixture', disabled: 'Desactivado' } },
    deliveries: { title: 'Entregas de notificaciones', hint: 'Un envío incierto puede haber llegado ya a la persona. Revisa la evidencia del proveedor antes de reintentar.', filter: 'Estado de entrega', filters: { DELIVERY_UNKNOWN: 'Inciertas o fallidas', BOUNCED: 'Rebotadas o suprimidas', SENT: 'Enviadas', ALL: 'Todas' }, empty: 'Ninguna entrega requiere atención.', attempts: '{{count}} intento(s)', retry: 'Reintentar', retryTitle: 'Reintentar esta entrega', retryHint: 'El mensaje puede enviarse de nuevo. El motivo queda en el registro de auditoría.', retried: 'Reintento en cola' },
    services: { title: 'Servicios y entornos', hint: 'Aprueba qué repositorios construyen cada servicio y qué entornos son producción. La cobertura solo usa mapeos aprobados.', empty: 'Todavía no se descubrieron servicios.', repositories: 'Repositorios', environments: 'Entornos', buildPath: 'Ruta de build (opcional)', buildPathFor: 'Ruta de build de {{name}}', classificationFor: 'Clasificación de {{name}}', noRepositories: 'Sin repositorios mapeados.', approve: 'Aprobar mapeo', approved: 'Mapeo aprobado', classes: { PRODUCTION: 'Producción', STAGING: 'Staging', DEVELOPMENT: 'Desarrollo', PREVIEW: 'Preview', OTHER: 'Otro' } },
    identities: { action: 'Cuentas vinculadas', title: 'Cuentas vinculadas', hint: 'Vincula una cuenta de GitHub o Jira solo cuando hayas confirmado que pertenece a esta persona. Los vínculos se auditan.', none: 'Sin cuentas vinculadas.', add: 'Vincular una cuenta', provider: 'Proveedor', search: 'Buscar por usuario o ID', candidates: 'Cuentas que se pueden vincular', historical: 'Historial de Jira', unmapped: 'Vista {{count}} vez/veces', noCandidates: 'No hay cuentas sin vincular.', link: 'Vincular cuenta', linked: 'Cuenta vinculada', unlink: 'Desvincular' },
    exc: { empty: 'No hay excepciones abiertas.', resolve: 'Resolver', ignore: 'Ignorar', open: 'Abrir', occurrences: '{{count}} ocurrencia(s)', filterKind: 'Tipo', all: 'Todos los tipos', kinds: { UNMATCHED_WORK: 'Trabajo sin asociar', UNCERTAIN_ASSOCIATION: 'Asociación incierta', UNMAPPED_IDENTITY: 'Identidad sin mapear', UNMAPPED_REPOSITORY: 'Repositorio sin mapear', UNMAPPED_SERVICE: 'Servicio sin mapear', CONNECTOR_FAILURE: 'Falla de conector', DUPLICATE_CORRECTION: 'Corrección de duplicado', WEBHOOK_INTEGRITY: 'Integridad de webhook', DEPLOYMENT_ORDERING: 'Orden de despliegues', DELIVERY_UNKNOWN: 'Entrega incierta', IMPORT_EXCEPTION: 'Excepción de importación', ACL_QUARANTINE: 'Cuarentena de acceso', SCOPE_REVIEW: 'Revisión de alcance', COMPLETION_BLOCKED: 'Finalización bloqueada' } },
    ops: { noAlerts: 'Sin alertas activas', outbox: 'Outbox pendiente', worker: 'Worker', healthy: 'Saludable', stale: 'Sin latido', aiTokens: 'Tokens de IA hoy', queue: 'Cola', queued: 'En cola', active: 'Activos', oldest: 'Espera más antigua', dead: 'Dead letters', replay: 'Reintentar', deadEmpty: 'No hay jobs en dead letter.' },
    imp: { mode: 'Modo', projects: 'Claves de proyecto (separadas por coma; vacío usa los proyectos aprobados)', start: 'Iniciar', queued: 'Importación encolada', resume: 'Reanudar', cancel: 'Cancelar', empty: 'Todavía no hay ejecuciones de importación.', exceptions: '{{count}} excepción(es)', mapping: 'Mapeos', mappingHelp: 'Primero ejecutá el descubrimiento. Aprobá cada proyecto antes de importar muestra, completo o delta. Los niveles de seguridad sin mapear quedan en cuarentena para administradores.', save: 'Guardar mapeos', saved: 'Mapeos guardados', approved: 'Aprobado', targetProject: 'Proyecto destino', targetTeam: 'Equipo de triage/responsable', none: 'Ninguno', requestTypes: 'Tipos importados como solicitudes', statusMap: 'Mapeo de estados', securityLevels: 'Niveles de seguridad', securityGroup: 'Visible para el grupo', quarantine: 'Cuarentena (solo administradores)', reconciliation: 'Reconciliación', modes: { DISCOVERY: 'Descubrimiento', DRY_RUN: 'Simulación', SAMPLE: 'Muestra', FULL: 'Importación completa', DELTA: 'Delta', RECONCILE: 'Reconciliar' }, statuses: { QUEUED: 'En cola', RUNNING: 'En curso', PAUSED: 'Pausada', SUCCEEDED: 'Completada', FAILED: 'Fallida', CANCELED: 'Cancelada' } },
    slackDest: { title: 'Destinos de progreso en Slack', help: 'Canales internos aprobados que reciben el progreso de un proyecto o equipo. Los ítems restringidos solo publican un enlace salvo que el destino esté aprobado para contenido restringido y todos sus miembros sean miembros plenos del workspace.', scope: 'Alcance', project: 'Proyecto', team: 'Equipo', workspace: 'ID del workspace', channel: 'ID del canal', restricted: 'Aprobado para contenido restringido', milestones: 'Hitos', add: 'Guardar destino', remove: 'Quitar', empty: 'No hay destinos configurados.', saved: 'Destino guardado', milestoneNames: { REQUEST_RECEIVED: 'Solicitud recibida', REQUEST_ACCEPTED: 'Solicitud aceptada', REQUEST_REJECTED: 'Solicitud rechazada', REQUEST_RESOLVED: 'Solicitud resuelta', REQUEST_NEEDS_ATTENTION: 'Requiere atención', WORK_CREATED: 'Tarea creada', WORK_STARTED: 'Tarea en curso', WORK_IN_REVIEW: 'Tarea en revisión', WORK_BLOCKED: 'Tarea bloqueada', WORK_INTEGRATED: 'Tarea integrada', WORK_DEPLOYED: 'Desplegada en producción', WORK_DELIVERED: 'Tarea completada', WORK_CANCELED: 'Tarea cancelada' } },
  },
  reports: {
    notes: {
      sourceNotConnected: '{{source}} no está conectado; sus valores son desconocidos, no cero.',
      sourceNotLive: 'Los datos de {{source}} vienen del modo {{mode}}, no son evidencia en vivo.',
      sourceNeverSynced: '{{source}} todavía no se sincronizó correctamente.',
      duplicatesIncluded: '{{count}} de estas solicitudes están marcadas como duplicadas.',
      reconsideredExcluded: '{{count}} decisiones reconsideradas en el período se excluyen de la tasa.',
      generationsAcrossRequests: '{{generations}} generaciones de cumplimiento en {{requests}} solicitudes distintas.',
      mixedBases: 'Bases de conclusión mezcladas ({{bases}}); elige una para comparar lo comparable.',
      partialCohorts: 'La ventana de seguimiento va desde cada primera conclusión hasta hoy; las cohortes recientes son parciales.',
      unattributedBugs_one: '{{count}} bug visible creado en el período, sin importar los filtros, no tiene causa informada; ya cerrados: {{closed}}.',
      unattributedBugs_other: '{{count}} bugs visibles creados en el período, sin importar los filtros, no tienen causa informada; ya cerrados: {{closed}}.',
      noDeliveryCause_one: '{{count}} bug visible creado en el período, sin importar los filtros, no tiene causa de entrega: {{breakdown}}.',
      noDeliveryCause_other: '{{count}} bugs visibles creados en el período, sin importar los filtros, no tienen causa de entrega: {{breakdown}}.',
      openWindow_one: '{{count}} elemento desplegado hace menos de {{days}} días aún puede recibir defectos en este período.',
      openWindow_other: '{{count}} elementos desplegados hace menos de {{days}} días aún pueden recibir defectos en este período.',
      caughtBeforeRelease_one: '{{count}} bug vinculado se encontró antes del despliegue de su origen y queda fuera.',
      caughtBeforeRelease_other: '{{count}} bugs vinculados se encontraron antes del despliegue de su origen y quedan fuera.',
      outsideWindow_one: '{{count}} bug vinculado se encontró más de {{days}} días después del despliegue de su origen y queda fuera.',
      outsideWindow_other: '{{count}} bugs vinculados se encontraron más de {{days}} días después del despliegue de su origen y quedan fuera.',
      originNotDeployed_one: '{{count}} bug vinculado indica trabajo sin una conclusión «{{basis}}» y queda fuera.',
      originNotDeployed_other: '{{count}} bugs vinculados indican trabajo sin una conclusión «{{basis}}» y quedan fuera.',
      currentState: 'Estado actual al momento de la consulta; para esta métrica no se reconstruyen estados pasados.',
      rolledBack: '{{count}} despliegues revertidos se muestran por separado.',
      noIncidentSource: 'No hay una fuente de incidentes conectada; el tiempo de recuperación no está disponible y nunca se infiere de los despliegues.',
      activityNotProductivity: 'Las contribuciones son indicadores de actividad, no una medida de productividad.',
      prAttribution: 'La autoría se indica por pull request, no por línea. Las exclusiones de archivos generados solo aplican con datos completos de archivos.',
      captureDenominator: 'Denominador: pull requests observados en repositorios vinculados a proyectos; el trabajo fuera de la plataforma no tiene denominador.',
      providerErrors: '{{count}} llamadas al proveedor fallaron.',
      weightCoverage: '{{scored}} puntuados, {{unscored}} sin puntuar (información insuficiente, pendientes o con error). No elegibles: {{historical}} históricos, {{restricted}} restringidos, {{platform}} registros de la plataforma, {{parent}} padres.',
      mixedQuestionVersions: 'Se mezclan puntuaciones de {{count}} versiones de preguntas en este período.',
      weightingNotLive: 'La ponderación del trabajo está en modo {{mode}}; los puntos no son juicios reales del modelo.',
    },
    filters: { any: 'Cualquiera', source: 'Origen', mine: 'Solo mis contribuciones', mineCauses: 'Solo trabajo asignado a mí', person: 'Persona', causeAssignee: 'Responsable de la causa' },
    defects: {
      open: 'Ver los defectos detrás de este número',
      title: 'Defectos escapados · {{group}}',
      unitBug: 'Cada fila es un bug, con todas las causas que lo cuentan aquí.',
      unitPair: 'Cada fila es un bug y una causa: un bug con varias causas en este grupo aparece una vez por causa, como en el conteo.',
      bug: 'Bug',
      created: 'Creado',
      cause: 'Causado por',
      teamProject: 'Equipo · proyecto',
      days: 'Tras el despliegue',
      daysValue_one: '{{count}} día',
      daysValue_other: '{{count}} días',
      showing_one: 'Mostrando {{shown}} de {{total}} defecto.',
      showing_other: 'Mostrando {{shown}} de {{total}} defectos.',
      empty: 'Ningún defecto corresponde ya a este número.',
      emptyHint: 'Los datos cambiaron desde el recuento; actualiza el reporte.',
    },
    chartLabel: 'Gráfico de {{metric}} por grupo',
    title: 'Reportes',
    subtitle: 'Métricas calculadas a partir de hechos registrados, con tu acceso aplicado. Los datos desconocidos nunca se muestran como cero.',
    metric: 'Métrica',
    period: 'Período',
    groupBy: 'Agrupar por',
    basis: 'Base de finalización',
    anyBasis: 'Todas las bases',
    firstOnly: 'Solo primera finalización',
    run: 'Ejecutar',
    export: 'Exportar CSV',
    exporting: 'Preparando exportación…',
    download: 'Descargar CSV',
    total: 'Total',
    count: 'Cantidad',
    unavailable: 'No disponible',
    median: 'Mediana',
    p85: 'Percentil 85',
    coverage: 'Cobertura',
    overlapping: 'Los grupos se superponen: una tarea puede estar en varios grupos, así que las filas no suman el total.',
    empty: 'No hay datos en este período.',
    tierLabel: 'Nivel',
    formula: 'Fórmula v{{version}}',
    watermark: 'Datos al {{time}}',
    definition: 'Definición',
    last7: 'Últimos 7 días',
    last30: 'Últimos 30 días',
    last90: 'Últimos 90 días',
    thisYear: 'Este año',
    from: 'Desde',
    to: 'Hasta',
    group: 'Grupo',
    value: 'Valor',
    ratio: '{{num}} de {{den}}',
    tiers: { GENERAL: 'General', TECHNICAL: 'Técnico', SENSITIVE: 'Sensible' },
    coverageStatus: { COMPLETE: 'Completa', PARTIAL: 'Parcial', UNKNOWN: 'Desconocida' },
    groups: { none: 'Sin agrupar', team: 'Equipo', project: 'Proyecto', type: 'Tipo', priority: 'Prioridad', week: 'Semana', month: 'Mes', repository: 'Repositorio', service: 'Servicio', person: 'Persona', outcome: 'Resultado', source: 'Origen' },
    metrics: {
      requests_created: { name: 'Solicitudes creadas', help: 'Solicitudes enviadas según fecha de envío.' },
      request_first_response: { name: 'Tiempo de primera respuesta', help: 'Desde el envío hasta la primera decisión humana de triage.' },
      request_acceptance_rate: { name: 'Tasa de aceptación', help: 'Primeras aceptaciones sobre aceptadas más rechazadas.' },
      requests_fulfilled: { name: 'Solicitudes resueltas', help: 'Solicitudes distintas resueltas en el período.' },
      request_lead_time: { name: 'Tiempo total de solicitud', help: 'Desde el envío hasta la resolución, por resolución.' },
      work_completed: { name: 'Trabajo completado', help: 'Ítems de trabajo distintos completados en el período.' },
      work_cycle_time: { name: 'Tiempo de ciclo', help: 'Desde el inicio hasta la finalización de cada episodio.' },
      work_in_progress: { name: 'Trabajo en curso', help: 'Ítems en curso, revisión o integrados ahora.' },
      blocked_time: { name: 'Tiempo bloqueado', help: 'Unión de intervalos bloqueados por ítem en el período.' },
      review_wait: { name: 'Espera de revisión', help: 'Desde el pedido de revisión hasta la decisión.' },
      integration_to_production: { name: 'De integración a producción', help: 'Desde la integración hasta la entrega verificada en producción.' },
      deployment_frequency: { name: 'Frecuencia de despliegue', help: 'Despliegues exitosos en producción.' },
      deployment_failure_rate: { name: 'Tasa de fallas de despliegue', help: 'Fallidos sobre exitosos más fallidos en producción.' },
      reopen_rate: { name: 'Tasa de reapertura', help: 'Trabajo completado que luego se reabrió.' },
      escaped_defects: { name: 'Defectos escapados', help: 'Bugs encontrados dentro de una ventana fija después de desplegar el trabajo que los causó.' },
      recovery_time: { name: 'Tiempo de recuperación', help: 'Requiere evidencia de incidentes; no disponible sin una fuente.' },
      contribution_activity: { name: 'Contribuciones', help: 'Contribuciones observadas de implementación, revisión y otras.' },
      code_additions: { name: 'Líneas agregadas', help: 'Adiciones reportadas por el proveedor en PRs fusionados.' },
      code_deletions: { name: 'Líneas eliminadas', help: 'Eliminaciones reportadas por el proveedor en PRs fusionados.' },
      capture_coverage: { name: 'Cobertura de captura', help: 'PRs observados vinculados a trabajo confirmado.' },
      requester_confirmation: { name: 'Confirmación del solicitante', help: 'Confirmado, persiste o sin respuesta por resolución.' },
      integrated_ai_usage: { name: 'Uso de IA', help: 'Tokens reportados por el proveedor para traducción, intake y ponderación del trabajo.' },
      weighted_work_completed: { name: 'Trabajo completado ponderado', help: 'Puntos de los elementos hoja completados, ponderados por TypeSafe Jev (1, 2, 3, 5, 8 o 13 cada uno).' },
    },
  },
  attachments: {
    title: 'Archivos',
    add: 'Adjuntar archivos',
    empty: 'Todavía no hay archivos adjuntos.',
    uploading: 'Subiendo {{name}}…',
    scanning: 'Analizando malware',
    rejected: 'Rechazado',
    download: 'Descargar',
    remove: 'Quitar archivo',
    removed: 'Archivo quitado',
    tooLarge: '{{name}} supera los {{max}} MiB.',
    tooMany: 'Podés adjuntar como máximo {{max}} archivos a la vez.',
    uploaded: '{{count}} archivo(s) subido(s); estarán disponibles después del análisis de malware.',
    pendingNote: 'Los archivos quedan en cuarentena hasta que termine el análisis.',
    video: 'Video',
    view: 'Ver {{name}}',
    viewer: 'Visor de archivos',
    viewerHint: 'Usá las flechas para pasar de un archivo a otro y Escape para cerrar.',
    counter: '{{index}} de {{total}}',
    previous: 'Archivo anterior',
    next: 'Archivo siguiente',
    actualSize: 'Tamaño real',
    fitToScreen: 'Ajustar a la pantalla',
    openInNewTab: 'Abrir en una pestaña nueva',
    loadFailed: 'Este archivo no se puede mostrar acá. Descargalo para abrirlo en tu dispositivo.',
  },
  savedViews: {
    title: 'Vistas',
    mine: 'Mis vistas',
    sharedWithEveryone: 'Vistas compartidas',
    shared: 'Compartida',
    empty: 'Todavía no hay vistas guardadas.',
    saveCurrent: 'Guardar filtros actuales…',
    name: 'Nombre de la vista',
    shareLabel: 'Compartir con todos (solo los filtros; cada persona sigue viendo únicamente lo que puede acceder)',
    hint: 'Una vista guardada conserva los filtros, nunca los resultados.',
    saved: 'Vista guardada',
    deleted: 'Vista eliminada',
    delete: 'Eliminar vista',
  },
  slack: {
    modalTitle: 'Crear solicitud Vammo',
    reviewTitle: 'Revisar solicitud',
    next: 'Siguiente',
    submit: 'Enviar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    privacyNote: 'Solo vos ves este formulario. La solicitud es privada hasta que la envíes.',
    fields: { title: 'Título corto', problem: '¿Qué está pasando?', expected: '¿Qué debería pasar en cambio?', impact: '¿A quién afecta y cuánto? (dejalo vacío si no lo sabés)' },
    impactUnknown: 'Desconocido por ahora',
    duplicates: 'Solicitudes posiblemente relacionadas',
    submitted: 'Solicitud *{{key}}* enviada. Seguila acá: {{url}}',
    dmHelp: 'Escribime en un mensaje lo que necesitás y lo convierto en un borrador de solicitud Vammo que revisás antes de enviar. También podés usar /vammo-work o el atajo "Create Vammo request" en cualquier mensaje.',
    dmDraftReady: 'Preparé un borrador de solicitud: *{{title}}*. Revisalo, completalo y envialo:',
    dmReview: 'Revisar y enviar',
    linkPrompt: 'Para crear solicitudes desde Slack, vinculá tu cuenta de Slack con tu cuenta de Vammo (enlace privado, válido por 10 minutos): {{url}}',
    notLinked: 'Tu cuenta de Slack todavía no está vinculada. Ejecutá /vammo-work de nuevo.',
    expired: 'Este borrador ya no está disponible. Empezá de nuevo con /vammo-work.',
    required: 'Obligatorio',
    unavailable: 'No se pudo abrir el formulario. Probá de nuevo o usá la aplicación web.',
    progress: '*{{key}}* {{title}} — {{status}}\n{{url}}',
    progressLinkOnly: 'Se actualizó un ítem seguido. Abrilo en Vammo Work: {{url}}',
    linkTitle: 'Vincular cuenta de Slack',
    linkBody: '¿Vincular el usuario de Slack {{handle}} con tu cuenta de Vammo? Las solicitudes que crees desde Slack se registrarán a tu nombre.',
    linkConfirm: 'Vincular cuenta',
    linkDone: 'Tu cuenta de Slack quedó vinculada. Podés cerrar esta pestaña y ejecutar /vammo-work de nuevo.',
    linkInvalid: 'Este enlace venció o ya se usó. Ejecutá /vammo-work en Slack para obtener uno nuevo.',
  },
  notifications: {
    preferences: {
      title: 'Configuración de notificaciones',
      event: 'Evento',
      email: 'Email',
      browser: 'Navegador',
      immediate: 'Al instante',
      digest: 'Resumen diario',
      muted: 'Desactivado',
      on: 'Activado',
      off: 'Desactivado',
    },
    events: {
      REQUEST_RECEIVED: 'Recibimos tu solicitud',
      REQUEST_NEEDS_INFORMATION: 'Se necesita más información',
      REQUEST_ACCEPTED: 'Tu solicitud fue aceptada',
      REQUEST_REJECTED: 'Tu solicitud no fue aceptada',
      REQUEST_WORK_STARTED: 'Empezó el trabajo en tu solicitud',
      REQUEST_RESOLVED: 'Tu solicitud fue resuelta',
      REQUEST_NEEDS_ATTENTION: 'Una solicitud requiere atención',
      WORK_ASSIGNED: 'Trabajo asignado a ti',
      WORK_BLOCKED: 'Algo que sigues está bloqueado',
      COMMENT_ADDED: 'Nuevo comentario en algo que sigues',
      MENTIONED: 'Te mencionaron',
      WORK_DUE_SOON: 'Trabajo tuyo vence pronto',
      WORK_OVERDUE: 'Trabajo tuyo está vencido',
      WORK_STALE: 'Trabajo tuyo sin actividad',
      WORK_AUTOMATION: 'Una regla automática te envió un aviso',
    },
    browser: {
      title: 'Notificaciones del navegador',
      enable: 'Activar en este navegador',
      disable: 'Desactivar en este navegador',
      test: 'Enviar una prueba',
      enabled: 'Las notificaciones del navegador están activadas',
      disabled: 'Las notificaciones están desactivadas en este navegador',
      testSent: 'Notificación de prueba enviada',
      testNone: 'Ningún navegador aceptó la prueba. Desactiva y vuelve a activar las notificaciones.',
      blockedHelp: 'Las notificaciones están bloqueadas para este sitio. Permítelas en la configuración del sitio del navegador (el ícono junto a la dirección).',
      devices: 'Navegadores que reciben notificaciones',
      unknownDevice: 'Navegador',
      forget: 'Olvidar',
      state: {
        unsupported: 'Este navegador no puede mostrar notificaciones. En iPhone y iPad, primero agrega el tracker a la pantalla de inicio.',
        blocked: 'Las notificaciones están bloqueadas para este sitio. Permítelas en la configuración del sitio del navegador y recarga la página.',
        off: 'Recibe asignaciones, menciones y novedades de lo que sigues, aunque el tracker esté cerrado.',
        on: 'Este navegador muestra tus notificaciones.',
      },
    },
  },
  push: {
    testTitle: 'Las notificaciones están activadas',
    testBody: 'Este navegador mostrará tus notificaciones de Vammo Work.',
  },
  email: {
    greeting: 'Hola {{name}},',
    open: 'Abrir en Vammo Work',
    footer: 'Recibes esto porque participas en este registro. Cambia las notificaciones en tu perfil.',
    actionConfirm: 'Sí, está resuelto',
    actionPersists: 'No, sigue pasando',
    actionComment: 'Dejar un comentario',
    confirmHint: 'Cuéntale al equipo si te funciona: abre la solicitud y elige "Sí, está resuelto" o "No, sigue pasando". Abrir este correo o enlace nunca registra una respuesta por sí solo.',
    digestSubject: 'Tu resumen de Vammo Work',
    layout: {
      kindRequest: 'Solicitud',
      kindWork: 'Trabajo',
      kindDocument: 'Documento',
      openRequest: 'Abrir la solicitud',
      openWork: 'Abrir el trabajo',
      openDocument: 'Abrir el documento',
      openMyWork: 'Abrir Mi trabajo',
      manageNotifications: 'Gestionar tus notificaciones',
      actionNeeded: 'Requiere acción',
      update: 'Novedad',
      digestHeadline: 'Tu resumen del día',
      digestIntro: 'Esto pasó en los registros que sigues ({{count}}).',
    },
    events: {
      REQUEST_RECEIVED: { subject: '[{{key}}] Recibimos tu solicitud', headline: 'Recibimos tu solicitud', body: 'Tu solicitud "{{title}}" fue recibida y espera triage.' },
      REQUEST_NEEDS_INFORMATION: { subject: '[{{key}}] Necesitamos más información', headline: 'El equipo necesita más información', body: 'El equipo necesita más información sobre "{{title}}".' },
      REQUEST_ACCEPTED: { subject: '[{{key}}] Tu solicitud fue aceptada', headline: 'Tu solicitud fue aceptada', body: 'Tu solicitud "{{title}}" fue aceptada. Aceptar no es una fecha de entrega; te avisaremos cuando empiece el trabajo.' },
      REQUEST_REJECTED: { subject: '[{{key}}] Tu solicitud no fue aceptada', headline: 'Tu solicitud no fue aceptada', body: 'Tu solicitud "{{title}}" no fue aceptada. Ábrela para leer el motivo.' },
      REQUEST_WORK_STARTED: { subject: '[{{key}}] Empezó el trabajo', headline: 'Empezó el trabajo en tu solicitud', body: 'Empezó el trabajo en "{{title}}".' },
      REQUEST_RESOLVED: { subject: '[{{key}}] Resuelta — confírmalo, por favor', headline: 'Tu solicitud fue resuelta', body: 'El equipo resolvió "{{title}}".' },
      REQUEST_NEEDS_ATTENTION: { subject: '[{{key}}] Requiere atención', headline: 'Una solicitud necesita tu atención', body: 'El solicitante indica que "{{title}}" sigue ocurriendo, o se perdió la entrega requerida.' },
      WORK_ASSIGNED: { subject: '[{{key}}] Asignado a ti', headline: 'Tienes trabajo nuevo asignado', body: '"{{title}}" te fue asignado.' },
      WORK_BLOCKED: { subject: '[{{key}}] Bloqueado', headline: 'Algo que sigues está bloqueado', body: '"{{title}}" está bloqueado.' },
      WORK_DONE: { subject: '[{{key}}] Terminado', headline: 'Algo que sigues está terminado', body: '"{{title}}" está terminado.' },
      COMMENT_ADDED: { subject: '[{{key}}] Nuevo comentario', headline: 'Nuevo comentario', body: 'Hay un nuevo comentario en "{{title}}".' },
      MENTIONED: { subject: '[{{key}}] Te mencionaron', headline: 'Te mencionaron', body: 'Te mencionaron en "{{title}}".' },
      WORK_DUE_SOON: { subject: '[{{key}}] Vence pronto', headline: 'El trabajo vence pronto', body: '"{{title}}" vence pronto.' },
      WORK_OVERDUE: { subject: '[{{key}}] Vencido', headline: 'El trabajo está vencido', body: '"{{title}}" pasó su fecha de vencimiento.' },
      WORK_STALE: { subject: '[{{key}}] Sin actividad reciente', headline: 'Trabajo sin actividad reciente', body: '"{{title}}" no cambia hace tiempo. Actualízalo o ciérralo si ya no hace falta.' },
      WORK_AUTOMATION: { subject: '[{{key}}] Aviso automático', headline: 'Una regla automática te envió un aviso', body: 'Una regla automática te envió un aviso sobre "{{title}}". Ábrelo para ver el mensaje.' },
    },
  },
  tutorial: {
    title: 'Tutorial',
    subtitle: 'Conecta tu asistente de IA, pide lo que necesitas, planifica el trabajo con tu equipo y deja que el trabajo de código se registre solo.',
    toc: 'En esta página',
    copy: 'Copiar',
    copied: 'Copiado',
    copyFailed: 'No se pudo copiar; selecciona el texto y cópialo a mano.',
    footer: 'Todo lo que hagas aquí, tú o tu asistente, se ejecuta con tus propios permisos.',
    intro: {
      title: 'Para qué sirve el tracker',
      lead: 'Vammo Work es donde vive todo el trabajo de Vammo: lo que la gente pide, las tareas que hacen los equipos y el código que las entrega. La idea es simple: **tu trabajo se registra solo**. Tú, o tu asistente de IA, hacen el trabajo; el tracker lo registra, lo vincula a la tarea correcta y lo sigue hasta que está funcionando en producción. Todos ven qué está pasando sin tener que preguntar ni actualizar tickets a mano.',
      requests: { title: 'Solicitudes', body: 'Cualquiera describe una necesidad o un problema (claves `REQ-`) y lo sigue hasta confirmar que quedó resuelto.' },
      work: { title: 'Trabajo', body: 'Los equipos convierten las necesidades en tareas (claves `VAM-`) y las siguen en listas y tableros, por equipo, proyecto o persona.' },
      agents: { title: 'Tu asistente lo mantiene al día', body: 'Con el plugin de Vammo, Claude o Codex abren la tarea, la pasan a en curso, registran los hitos y vinculan ramas, commits y pull requests mientras programas.' },
      delivery: { title: 'La entrega se verifica', body: 'El trabajo está terminado cuando el tracker ve su código desplegado en producción, no cuando alguien lo dice.' },
      start: 'Lo primero: **conecta tu asistente de IA** aquí abajo. Lleva un par de minutos y es lo que hace que el seguimiento sea automático.',
    },
    connect: {
      title: 'Conecta tu asistente de IA',
      intro: 'El tracker es un servidor MCP. Una vez conectado, tu asistente puede crear solicitudes, crear y actualizar trabajo y registrar lo que hace, siempre como tú y con tus permisos. Inicias sesión con tu cuenta de Vammo; no hay ninguna API key que copiar.',
      serverUrl: 'URL del servidor',
      pluginTitle: '¿Qué se instala?',
      plugin: 'El **plugin** de Codex y Claude Code agrega la conexión MCP y una habilidad que guía al asistente para registrar el trabajo. Claude Code también recibe un recordatorio al iniciar la sesión. La configuración de ChatGPT de abajo crea un plugin desde el servidor MCP, con herramientas e instrucciones. El trabajo se registra cuando el asistente llama a esas herramientas; instalarlo no registra todas las conversaciones.',
      claudeDesktop: {
        name: 'Claude (web y escritorio)',
        summary: 'El chat de Claude en claude.ai y en la app de Windows y macOS. Con tu cuenta de la empresa el plugin ya viene instalado; solo lo conectas una vez.',
        s1: 'En Claude, abre **Customize → Plugins** y verifica que esté **Vammo Work Tracker**. Viene instalado con tu cuenta de la empresa.',
        s2: 'Abre el plugin, ve a su pestaña **Connectors** y elige **Connect**. Se abre el inicio de sesión de Vammo en el navegador: entra con tu cuenta de Google de Vammo y aprueba el acceso solicitado.',
        s3: 'Configura los permisos de herramientas del conector: **Permitir siempre** para las herramientas que leen y registran trabajo (`get_…`, `search_…`, `ensure_work_item`, `start_work_session`, `link_code`, `record_progress`, `finish_work_session`, `add_comment`…). Deja **Necesita aprobación** para `write_operation`, que puede hacer todo lo que tú puedes hacer en la web, incluso borrar.',
        s4: 'En un chat nuevo, abre el menú de herramientas (**+**) y verifica que **Vammo Work Tracker** esté activado. La skill del plugin necesita **ejecución de código**: si Claude no la sigue, activa la ejecución de código en **Settings → Capabilities**.',
        s5: '¿Usas una cuenta personal de Claude? Ahí no está el plugin: abre **Settings → Connectors → Add custom connector**, llámalo `Vammo Work Tracker`, pega la URL del servidor, conéctalo y configura los permisos como en el paso 3:',
        s6: 'Opcional: pega estas instrucciones en un proyecto de Claude para que siempre trabaje con el tracker:',
        instructions: 'Use the Vammo Work Tracker connector for requests and work. Call get_context first. To file a request, call prepare_request with what I describe, show me the draft and missing fields, and only call submit_request after I confirm. When a request I filed is resolved, ask me whether it is fixed and record confirm_request. Never invent facts, priorities or deployment status.',
      },
      claudeCode: {
        name: 'Claude Code (terminal)',
        summary: 'Claude en tu terminal, tu IDE o la pestaña Code de la app de escritorio. Con tu cuenta de la empresa, Claude Code instala el plugin solo al iniciar; trae el servidor, la skill de seguimiento y un recordatorio, así el trabajo de código se registra automáticamente en todos tus repositorios.',
        s1: '¿Usas una cuenta personal de Claude? Instala el plugin una vez (con la cuenta de la empresa no hace falta):',
        s2: 'Reinicia Claude Code, ejecuta `/mcp`, selecciona `plugin:vammo-work-tracker:vammo` y elige **Authenticate**. Inicia sesión con tu cuenta de Google de Vammo en el navegador. Se hace una vez por computadora.',
        s3: 'La primera vez que Claude usa cada herramienta de Vammo te pide permiso. Responde **Yes, and don’t ask again** para las herramientas que leen y registran trabajo; para `write_operation`, que puede hacer todo lo que tú puedes hacer en la web, deja que siga preguntando.',
        s4: 'Si antes configuraste el tracker a mano, ejecuta `claude mcp remove vammo` y borra `~/.claude/skills/vammo-work-tracking/`, así no quedan herramientas duplicadas.',
        s5: 'Mantén el plugin actualizado. Con la cuenta de la empresa se actualiza solo; con una cuenta personal, ejecuta esto y reinicia Claude Code:',
      },
      chatgptDesktop: {
        name: 'ChatGPT (web)',
        summary: 'Empezá en chatgpt.com, en el espacio de trabajo donde vas a usar el tracker. Si Vammo Work Tracker ya aparece en Plugins, instalalo y conectá tu cuenta.',
        s1: 'Abrí **Plugins**, elegí **+** y después **Add custom MCP server** (agregar servidor MCP personalizado).',
        s2: 'Poné el nombre **Vammo Work Tracker**, pegá la URL y elegí **OAuth**. Si pide un método de registro, elegí **Dynamic client registration (DCR)**. No necesitás una API key ni ingresar un secreto de cliente:',
        s3: 'Revisá el aviso de acceso, elegí **Create as a plugin** y después **Install**. Cuando pida conectar la cuenta, iniciá sesión con tu Google de Vammo y aprobá el acceso.',
        s4: 'En un chat nuevo, escribí **@** y seleccioná **Vammo Work Tracker**. Pedile que llame a `get_context` y comprobá que devuelva tu nombre.',
        note: 'Los menús y la disponibilidad dependen del espacio de trabajo y la versión. Si no permite servidores MCP personalizados, pedile al administrador que los habilite. Configurá y verificá primero en la web. Los permisos para ejecutar herramientas dependen de ChatGPT y son distintos del inicio de sesión.',
      },
      codex: {
        name: 'Codex (CLI e IDE)',
        summary: 'Usá una versión actual de Codex CLI en la misma computadora y perfil de usuario que tu IDE. Instalar y conectar tu cuenta de Vammo son dos pasos distintos. Estos comandos se verificaron con Codex CLI 0.162.0.',
        s1: 'Instalá el plugin una vez. Agrega el servidor y la habilidad para registrar trabajo, sin necesitar acceso a un repositorio privado de GitHub:',
        s2: 'Conectá tu cuenta de Vammo. En el navegador, iniciá sesión con tu Google de Vammo y aprobá el acceso. Esperá la confirmación de éxito en la terminal:',
        s3: 'Abrí una sesión nueva de Codex. Si usás la extensión del IDE, reiniciala para que cargue el plugin y la conexión.',
        s4: 'Verificá que `vammo` esté habilitado y use OAuth; después hacé la prueba de conexión de abajo. Aparecer en la lista no demuestra que el login funcione:',
        maintenance: 'Actualizaciones e instalaciones manuales anteriores',
        update: 'Cuando haya una actualización del plugin, actualizá el marketplace, revisá la versión instalada con `codex plugin list` y reiniciá Codex:',
        migration: 'Si las herramientas aparecen duplicadas por una instalación manual anterior, guardá una copia de la configuración. Cuando el plugin funcione, quitá solo la entrada manual vieja `[mcp_servers.vammo]` y la habilidad duplicada que habías copiado. Conservá los archivos del plugin.',
      },
      verifyTitle: 'Verifica la conexión',
      verify: 'Pedile: “Usá Vammo Work Tracker para llamar a get_context y decirme mi nombre y permisos”. Comprobá que la llamada a la herramienta funcione con tu cuenta. Tener el servidor configurado o el plugin instalado no alcanza para verificar la conexión.',
      sessionTitle: 'Mantener la sesión iniciada',
      session: 'El cliente renueva el acceso en segundo plano. El tracker permite 90 días entre renovaciones, así que el uso habitual mantiene la conexión aunque cierres el navegador. Volvé a iniciar sesión en un dispositivo o perfil nuevo, si se borraron las credenciales, después de 90 días sin renovación o si se revocó el acceso. Las cuentas suspendidas no pueden conectarse. Aprobar una herramienta es distinto de iniciar sesión.',
      troubleTitle: 'Si falla la conexión',
      trouble: 'Si faltan herramientas, reiniciá Codex o actualizá el plugin en ChatGPT y probá get_context otra vez. Ante un error de autenticación, ejecutá `codex mcp login vammo` o reconectá el plugin de ChatGPT una vez. Que pida login cada pocos minutos es una falla: pasale al administrador del tracker la versión del cliente, la hora y el error. No envíes tokens ni URLs de retorno del login.',
      adminNote: 'Tu espacio de trabajo debe permitir conexiones MCP a esta URL. Conectar ChatGPT no inicia sesión en una instalación separada de Codex.',
    },
    request: {
      title: 'Pide una nueva funcionalidad',
      intro: 'Cualquiera puede pedir una funcionalidad o reportar un problema. No necesitas saber cómo construirlo: describe qué necesitas y por qué. Cada solicitud recibe una clave `REQ-`.',
      web: { title: 'Desde la web', body: 'Elige **Nueva solicitud** o presiona `R`. La pestaña **Guiado** te hace preguntas en lenguaje simple; la pestaña **Formulario** te deja completar los campos directamente. Los borradores son privados hasta que los envías.' },
      assistant: { title: 'Desde Claude o ChatGPT', body: 'Di, por ejemplo: “Crea una solicitud: los conductores quieren ver su historial de swaps en la app.” El asistente prepara un borrador, te muestra lo que falta y solo lo envía cuando confirmas.' },
      slack: { title: 'Desde Slack', body: 'Ejecuta `/vammo-work` o usa el atajo **Create Vammo request** sobre cualquier mensaje. La primera vez, Slack te envía un enlace privado para conectar tu cuenta.' },
      tipsTitle: 'Qué hace buena a una solicitud',
      t1: 'Qué pasa hoy y qué debería pasar en cambio.',
      t2: 'A quién afecta y cuánto importa; “todavía no lo sé” es una respuesta válida.',
      t3: 'Capturas, enlaces o ejemplos como adjuntos.',
      after: 'Triage acepta la solicitud y la vincula a trabajo, y recibes avisos a medida que avanza. Cuando se entrega, te preguntan si resolvió tu problema.',
    },
    team: {
      title: 'Crea tareas para tu equipo',
      intro: 'Los ítems de trabajo (claves `VAM-`) son las tareas que ejecuta un equipo. Una solicitud describe una necesidad; un ítem de trabajo describe qué se va a hacer al respecto.',
      s1: 'Elige **Nuevo ítem de trabajo** o presiona `C`.',
      s2: 'Escribe un título corto y elige el tipo: funcionalidad, bug, mantenimiento, investigación, etc.',
      s3: 'En **Responsable**, elige el equipo (o **Personal** para tu propio trabajo). Opcionalmente asigna una persona, un proyecto y una prioridad.',
      s4: 'Agrega criterios de aceptación: los resultados observables que indican que la tarea está terminada.',
      s5: 'Para atender una solicitud, ábrela y elige **Nuevo ítem de trabajo** desde ahí, así quien la pidió sigue el avance automáticamente.',
      s6: 'Sigue el avance en **Trabajo** como lista o tablero, filtrando por equipo, proyecto o responsable.',
      assistantTitle: 'O pídeselo a tu asistente',
      assistant: '“Crea una tarea para el equipo de Plataforma para agregar un filtro al historial de swaps, prioridad alta.” El asistente te dice qué va a crear y te pide confirmación antes.',
    },
    notifications: {
      title: 'Activa las notificaciones',
      intro: 'Entérate cuando tus solicitudes avanzan, cuando te asignan trabajo o cuando alguien te menciona, sin tener el tracker abierto. Todo aparece además en la lista de notificaciones dentro del tracker.',
      open: 'Configurar notificaciones',
      s1: 'Abre **Configuración → Notificaciones**, o elige la campana arriba a la derecha.',
      s2: 'En **Notificaciones del navegador**, elige **Activar en este navegador** y acepta cuando el navegador pida permiso.',
      s3: 'Elige **Enviar una prueba** para comprobarlo. Repítelo en cada computadora o teléfono donde quieras recibirlas; la misma tarjeta lista tus navegadores para que olvides los viejos.',
      s4: 'En **Configuración de notificaciones**, elige para cada evento: email **Al instante**, **Resumen diario** o **Desactivado**, y navegador **Activado** o **Desactivado**.',
      browser: { title: 'Navegador', body: 'Avisos emergentes en este dispositivo, aunque la pestaña del tracker esté cerrada. Se activan por navegador y por dispositivo.' },
      email: { title: 'Email', body: 'Activado por defecto para tus solicitudes, asignaciones y menciones. **Resumen diario** agrupa las novedades del día en un solo email.' },
      slack: { title: 'Slack', body: 'Las solicitudes creadas desde Slack reciben su avance en el mismo hilo. Para publicar además las novedades de un proyecto en un canal, mira **Usa el tracker desde Slack** más abajo.' },
      troubleTitle: 'Si no te llegan las notificaciones',
      blocked: 'Si el navegador las bloqueó, permite las notificaciones en la configuración del sitio (el ícono junto a la dirección) y recarga la página.',
      iphone: 'En iPhone o iPad, primero agrega el tracker a la pantalla de inicio (**Compartir → Agregar a inicio**) y actívalas desde ese ícono.',
      missing: 'Si no ves **Notificaciones del navegador** en Configuración, todavía no están habilitadas en este entorno; el email y la lista dentro de la app siguen funcionando.',
    },
    slack: {
      title: 'Usa el tracker desde Slack',
      intro: 'Sigue las novedades de un proyecto en un canal de Slack y pídele a @leonardo que cree y consulte trabajo sin salir de la conversación.',
      channelTitle: 'Publica las novedades de un proyecto en un canal',
      c1: 'En Slack, abre el canal e invita a la app. Sin esto no puede publicar ahí, y los canales privados ni siquiera aparecen en la lista:',
      c2: 'En el tracker, abre el proyecto, elige **Configuración** y ve a **Novedades en Slack**. Elige el canal de la lista de canales que la app puede ver.',
      c3: 'Elige qué novedades publicar: tareas creadas, en curso, en revisión, bloqueadas, integradas, **desplegadas en producción**, completadas o canceladas, y los hitos de las solicitudes. Un canal de releases, por ejemplo, puede seguir solo **Desplegada en producción**.',
      c4: 'Elige **Guardar canal de Slack**. Para dejar de publicar, elige **Ningún canal** y guarda. Pueden cambiarlo los responsables del proyecto y los administradores. Cada tarea tiene un mensaje en el canal, y sus novedades siguientes van en ese hilo.',
      leonardoTitle: 'Pídeselo a @leonardo',
      leonardo: '**@leonardo** es el agente de Vammo en Slack y está conectado al tracker. Menciónalo en un canal o en un hilo y trabaja como tú, con tus permisos. Por ejemplo:',
      e1: 'Crear una solicitud con lo que describes: “@leonardo crea una solicitud: los conductores no ven su historial de swaps.”',
      e2: 'Crear una tarea para un equipo: “@leonardo crea una tarea para Plataforma para agregar un filtro al historial de swaps, prioridad alta.”',
      e3: 'Leer una conversación entera y convertirla en trabajo: “@leonardo lee este hilo y crea las tareas que salen de acá para Fleet Operations.”',
      e4: 'Consultar: “@leonardo ¿en qué estado está VAM-42?” o “¿qué sigue abierto en Rider App?”',
      e5: 'Asignar y mencionar a las personas de la conversación: reconoce a los miembros de Slack por su email de la empresa.',
      noteTitle: 'Para tener en cuenta',
      note: 'Lo que hace @leonardo aparece en el historial a tu nombre (vía Clawbot), y nunca tiene permisos de administrador. El comando `/vammo-work` y el atajo **Create Vammo request** siguen funcionando.',
    },
    tracking: {
      title: 'El trabajo de código se registra solo',
      intro: 'No necesitas actualizar tickets a mano. Cuando programas con un asistente conectado o abres pull requests, el tracker registra el trabajo y lo vincula a la tarea correcta.',
      f1: { title: 'Empiezas una tarea', body: 'Con el plugin instalado, el asistente registra tu objetivo, repositorio y rama. Reutiliza la tarea que nombras (por ejemplo `VAM-42`) o la que ya está vinculada a tu rama o sesión, y crea una nueva solo cuando nada coincide. La tarea pasa sola a **En curso**. Las tareas que un agente crea por su cuenta muestran un ícono de robot, y el filtro **Creadas por agentes** las lista para que el equipo las revise.' },
      f2: { title: 'Se registran los hitos', body: 'El avance, las verificaciones que realmente ejecutó, los bloqueos y los pull requests se agregan a la tarea a medida que avanza la sesión. Un archivo pequeño dentro de `.git/` recuerda el vínculo entre sesiones y nunca se commitea.' },
      f3: { title: 'GitHub lo confirma', body: 'Un pull request cuyo título, rama o descripción menciona una clave como `VAM-42`, o cuya rama coincide con una sesión de agente, se vincula automáticamente. Asocia el repositorio a un proyecto; además de los webhooks, el tracker reconcilia cada 15 minutos.' },
      f4: { title: 'La entrega se verifica', body: 'Cuando todos los pull requests vinculados se mergean, el trabajo de producción pasa a integrado. Llega solo a **Terminado** cuando el tracker ve un despliegue en producción que contiene su código; nadie tiene que declarar qué servicios toca. Un PR mergeado o una sesión terminada no alcanzan.' },
      keyTipTitle: 'Consejo: pon la clave en la rama',
      keyTip: 'Una rama como `feat/VAM-42-swap-history`, o `VAM-42` en el título del pull request, vincula el trabajo con certeza, incluso sin asistente.',
      ambiguousTitle: 'Cuando la coincidencia es dudosa',
      ambiguous: 'Los títulos parecidos son solo sugerencias. Si varias tareas podrían coincidir, el asistente te muestra las candidatas y te pregunta. Si el trabajo tiene que seguir mientras tanto, crea un ítem provisional que aparece en la bandeja de excepciones para confirmarlo o fusionarlo; no se pierde nada.',
    },
  },
  table: {
    total_one: '{{count}} elemento',
    total_other: '{{count}} elementos',
    showing_one: '{{count}} cargado',
    showing_other: '{{count}} cargados',
    sortBy: 'Ordenar por {{column}}',
    ascending: 'Orden ascendente',
    descending: 'Orden descendente',
    people: 'Personas',
    peopleRole: 'Rol',
    clearPeople: 'Quitar personas',
    selected: 'Seleccionado',
    activeFilters: 'Filtros activos',
    removeFilter: 'Quitar filtro',
    noOwner: 'Nadie',
    createdBy: 'Creado por',
    empty: 'Vacío',
    due: {
      overdue: 'Vencidos',
      soon: 'Próximos 7 días',
      none: 'Sin fecha límite',
      set: 'Con fecha límite',
    },
    editMode: 'Editar en la tabla',
    editModeOn: 'Edición en la tabla: los cambios se guardan al hacerlos. Abre un elemento desde su título.',
    saved: 'Guardado',
    phaseMoveHint: 'Mover a…',
    searchPeople: 'Buscar personas',
  },
  fields: {
    documentsTitle: 'Propiedades de documentos',
    documentsHint: 'Propiedades que puede tener cualquier documento, como estado, responsable o tipo. También filtran la tabla de documentos.',
    title: 'Campos personalizados',
    projectTitle: 'Campos del proyecto',
    hint: 'Campos con tipo en los elementos de trabajo. Archivar un campo lo oculta; sus valores se conservan.',
    manage: 'Gestionar campos',
    section: 'Campos',
    name: 'Nombre',
    type: 'Tipo',
    add: 'Agregar campo',
    created: 'Campo creado',
    empty: 'Todavía no hay campos.',
    archived: 'Archivado',
    archive: 'Archivar campo',
    restore: 'Restaurar campo',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    optionName: 'Opción',
    newOption: 'Nueva opción',
    addOption: 'Agregar',
    removeOption: 'Quitar {{name}}',
    none: 'No hay campos para este elemento.',
    filterTitle: 'Campo',
    global: 'Todo el trabajo',
    types: {
      TEXT: 'Texto',
      NUMBER: 'Número',
      BOOLEAN: 'Sí / no',
      DATE: 'Fecha',
      SINGLE_SELECT: 'Opción única',
      MULTI_SELECT: 'Opción múltiple',
    },
  },
  calendar: {
    title: 'Calendario',
    previous: 'Mes anterior',
    next: 'Mes siguiente',
    today: 'Hoy',
    count_one: '{{count}} elemento vence en esta vista',
    count_other: '{{count}} elementos vencen en esta vista',
    hint: 'Arrastra un elemento para cambiar su fecha límite',
    more_one: '+{{count}} más',
    more_other: '+{{count}} más',
  },
  similar: {
    title: 'Trabajo parecido que ya existe',
    aiTitle: 'Posibles duplicados',
    aiNone: 'La IA no encontró duplicados entre los registros parecidos.',
    check: 'Revisar con IA',
    checkRequests: 'Buscar duplicados con IA',
    noTextMatches: 'No hay solicitudes parecidas por texto.',
    byAi: 'IA',
    fixture: 'Modo de prueba',
    confidence: {
      HIGH: 'Lo mismo',
      MEDIUM: 'Probablemente lo mismo',
      LOW: 'Texto parecido',
    },
  },
  triageAssist: {
    title: 'Sugerencia de triage con IA',
    hint: 'Propone prioridad, equipo, proyecto, tipo y etiquetas a partir de la solicitud. Nada cambia hasta que lo apliques.',
    suggest: 'Sugerir',
    again: 'Volver a sugerir',
    apply: 'Aplicar',
    ask: 'Preguntar a quien pidió',
    projectHint: 'Muévela con el campo de proyecto para revisar quién la verá.',
    createWork: 'Crear trabajo con esto',
  },
  automations: {
    title: 'Automatizaciones',
    subtitle: 'Reglas que actúan sobre el trabajo cuando algo pasa: asignar, priorizar, etiquetar, poner fechas o avisar.',
    new: 'Nueva regla',
    edit: 'Editar regla',
    dialogHint: 'Las reglas actúan como el sistema, como mucho una vez por cambio, y sus propios cambios nunca disparan reglas.',
    scope: 'Alcance',
    allScopes: 'Todas las reglas que puedo ver',
    showArchived: 'Mostrar archivadas',
    empty: 'Todavía no hay reglas',
    emptyHint: 'Los responsables de proyecto crean reglas para sus proyectos; los administradores, para todo el trabajo.',
    name: 'Nombre',
    trigger: 'Disparador',
    when: 'Cuando',
    if: 'si',
    ifTitle: 'Si',
    then: 'Entonces',
    any: 'Cualquiera',
    phases: 'Fase actual',
    fromPhases: 'Fase anterior',
    labelsAny: 'Tiene alguna de estas etiquetas',
    assignment: 'Asignación',
    unassignedYes: 'sin asignar',
    unassignedNo: 'con alguien asignado',
    action: 'Acción',
    addAction: 'Agregar acción',
    removeAction: 'Quitar acción',
    enabled: 'Activa',
    saved: 'Regla guardada',
    archive: 'Archivar regla',
    restore: 'Restaurar regla',
    runs: 'Ejecuciones recientes',
    runsTitle: 'Ejecuciones de {{name}}',
    noRuns: 'Esta regla todavía no se ejecutó.',
    hiddenItem: 'Un elemento que no puedes ver',
    runCount_one: 'Se ejecutó {{count}} vez',
    runCount_other: 'Se ejecutó {{count}} veces',
    lastRun: 'última',
    lastError: 'Último error: {{error}}',
    runStatus: {
      APPLIED: 'Aplicada',
      FAILED: 'Falló',
    },
    unassign: 'Nadie (quitar asignación)',
    days: 'Días',
    daysAfter: 'días después del cambio',
    onlyIfEmpty: 'Solo si no tiene fecha límite',
    chooseField: 'Elige un campo',
    message: 'Mensaje',
    messagePlaceholder: '¿Qué deben saber o hacer?',
    addPerson: 'Agregar persona',
    people_one: '{{count}} persona',
    people_other: '{{count}} personas',
    manage: 'Reglas automáticas',
    byRule: 'Automatización: {{name}}',
    triggers: {
      WORK_CREATED: 'se crea trabajo',
      PHASE_CHANGED: 'cambia la fase',
      PRIORITY_CHANGED: 'cambia la prioridad',
      ASSIGNEE_CHANGED: 'cambia la persona asignada',
      LABELS_CHANGED: 'cambian las etiquetas',
      DUE_DATE_CHANGED: 'cambia la fecha límite',
      BLOCKED: 'el trabajo se bloquea',
    },
    actions: {
      SET_PRIORITY: 'Poner prioridad',
      SET_ASSIGNEE: 'Asignar a',
      ADD_LABELS: 'Agregar etiquetas',
      REMOVE_LABELS: 'Quitar etiquetas',
      SET_DUE_IN_DAYS: 'Poner fecha límite',
      SET_FIELD: 'Completar un campo',
      NOTIFY: 'Avisar',
    },
    recipients: {
      ASSIGNEE: 'Persona asignada',
      OWNER: 'Dueño',
      CREATOR: 'Quien lo creó',
      FOLLOWERS: 'Seguidores',
    },
    text: {
      SET_PRIORITY: 'poner prioridad {{value}}',
      SET_ASSIGNEE: 'asignar a {{value}}',
      UNASSIGN: 'quitar la asignación',
      ADD_LABELS: 'agregar etiquetas {{value}}',
      REMOVE_LABELS: 'quitar etiquetas {{value}}',
      SET_DUE_IN_DAYS_one: 'poner fecha límite {{count}} día después',
      SET_DUE_IN_DAYS_other: 'poner fecha límite {{count}} días después',
      SET_DUE_IF_EMPTY_one: 'si no tiene fecha límite, poner una {{count}} día después',
      SET_DUE_IF_EMPTY_other: 'si no tiene fecha límite, poner una {{count}} días después',
      SET_FIELD: 'poner {{field}} en',
      NOTIFY: 'avisar a {{who}}: "{{message}}"',
    },
  },
  dashboards: {
    title: 'Tableros',
    subtitle: 'Tus propias vistas del trabajo: conteos, gráficos y listas sobre cualquier filtro. Cada persona ve solo lo que puede ver.',
    choose: 'Tablero',
    new: 'Nuevo tablero',
    name: 'Nombre',
    withStarter: 'Empezar con widgets útiles',
    empty: 'Todavía no hay tableros',
    emptyHint: 'Crea uno con el trabajo abierto, lo vencido, la carga por persona y la tendencia semanal, y después ajústalo.',
    createStarter: 'Crear mi primer tablero',
    sharedTag: 'compartido',
    share: 'Compartido con todos',
    edit: 'Editar',
    doneEditing: 'Listo',
    more: 'Más acciones',
    rename: 'Renombrar',
    copy: 'Hacer una copia',
    copyName: '{{name}} (copia)',
    delete: 'Eliminar tablero',
    confirmDelete: '¿Eliminar "{{name}}"? Se quitan los widgets; el trabajo no cambia.',
    noWidgets: 'Este tablero todavía no tiene widgets',
    addWidget: 'Agregar widget',
    editWidget: 'Editar widget',
    removeWidget: 'Quitar widget',
    moveBack: 'Mover antes',
    moveForward: 'Mover después',
    saved: 'Tablero guardado',
    widgetTitle: 'Título',
    kind: 'Widget',
    size: 'Tamaño',
    groupBy: 'Agrupar por',
    limit: 'Elementos',
    weeks: 'Semanas',
    filters: 'Qué trabajo',
    filtersHint: 'Los mismos filtros que la lista de trabajo. "Quien lo esté viendo" hace el widget personal para cada persona.',
    fromView: 'Copiar filtros de una vista guardada…',
    viewer: 'Quien lo esté viendo',
    none: 'Ninguno',
    noData: 'Nada coincide con estos filtros.',
    items: 'Elementos',
    created: 'Creados',
    completed: 'Completados',
    seeAll: 'Ver los {{count}}',
    kinds: {
      COUNT: 'Número',
      BAR: 'Gráfico de barras',
      PIE: 'Gráfico de anillo',
      LIST: 'Lista',
      TREND: 'Tendencia semanal',
    },
    sizes: {
      S: 'Chico',
      M: 'Media fila',
      L: 'Fila completa',
    },
    groups: {
      phase: 'Fase',
      priority: 'Prioridad',
      type: 'Tipo',
      assignee: 'Persona asignada',
      owner: 'Dueño',
      project: 'Proyecto',
      team: 'Equipo',
      label: 'Etiqueta',
    },
    sorts: {
      updatedAt: 'Actualizados recientemente',
      dueDate: 'Fecha límite',
      priority: 'Prioridad',
      createdAt: 'Más nuevos',
    },
    starter: {
      name: 'Mi tablero',
      open: 'Trabajo abierto',
      overdue: 'Vencidos',
      blocked: 'Bloqueados',
      byPhase: 'Trabajo por fase',
      byAssignee: 'Trabajo abierto por persona',
      trend: 'Creados y completados por semana',
      urgent: 'Urgente y prioridad alta',
    },
  },
  agentCreated: {
    label: 'Creado por un agente',
    origin: 'Origen',
    filter: 'Creadas por agentes',
    filterPeople: 'Creadas por personas',
    hint: 'Un agente de IA lo creó por su cuenta para registrar su trabajo. Lo que creas tú, o le pides a un agente que cree, no lleva robot.',
    provisionalHint: 'Puede duplicar trabajo existente: confírmalo o fusiónalo.',
  },
  workWeight: {
    unit: 'pts',
    scored: 'Tamaño estimado automáticamente por TypeSafe Jev a partir del ítem y sus pull requests al completarse (1, 2, 3, 5, 8 o 13). Reabrirlo no suma puntos: el ítem conserva su puntuación más alta.',
    parent: 'Suma de los puntos de sus subelementos.',
    zero: '0 puntos: no había pull request, commit ni progreso vinculado al completarse.',
    partial: 'Algunas finalizaciones aún no tienen puntuación; los puntos actuales todavía pueden subir.',
    pending: 'Aún sin puntuar; la puntuación se calcula unos 25 minutos después de completarse.',
    unscored: 'No se pudo puntuar: poca información o la puntuación falló.',
  },
  noEvidence: {
    badge: 'Sin evidencia',
    hint: 'Una persona lo cerró sin evidencia de despliegue ni de entregable',
    filter: 'Cerrados sin evidencia',
    dialogHint: 'El elemento pasa a Terminado y queda marcado "Sin evidencia". Explica por qué está terminado, por ejemplo dónde se desplegó o se comprobó.',
  },
  databases: {
    title: 'Título de la base de datos',
    untitled: 'Sin título',
    restricted: 'Base de datos restringida',
    restrictedRow: 'Restringido',
    loadFailed: 'No se pudo cargar la base de datos.',
    menu: 'Acciones de la base de datos',
    openFullPage: 'Abrir en página completa',
    inDocument: 'En {{name}}',
    archive: 'Archivar base de datos',
    archiveConfirm: '¿Archivar esta base de datos? Queda de solo lectura dondequiera que se muestre, y sus editores pueden restaurarla.',
    archivedNotice: 'Esta base de datos está archivada y es de solo lectura.',
    archivedBadge: 'Archivada',
    archivedToast: 'Base de datos archivada',
    restore: 'Restaurar',
    restoredToast: 'Base de datos restaurada',
    removeBlock: 'Quitar de este documento',
    search: 'Buscar',
    noMatches: 'Ninguna fila coincide.',
    loadMore: 'Mostrar más ({{shown}} de {{total}})',
    rowCount_one: '{{count}} fila',
    rowCount_other: '{{count}} filas',
    export: {
      more: '… {{count}} filas más',
    },
    defaults: {
      title: 'Base de datos sin título',
      name: 'Nombre',
      status: 'Estado',
      notStarted: 'Sin empezar',
      inProgress: 'En curso',
      done: 'Hecho',
      tableView: 'Tabla',
      boardView: 'Tablero',
      listView: 'Lista',
      calendarView: 'Calendario',
      untitledRow: 'Sin título',
      relatedRows: 'Relacionados',
    },
    blocks: {
      section: 'Bases de datos',
      table: 'Base de datos — tabla',
      board: 'Base de datos — tablero',
      linked: 'Base de datos vinculada',
      linkedHint: 'Muestra aquí también una base de datos que puedes leer. Son los mismos datos: los cambios se ven en todas partes y las personas siguen necesitando acceso a su documento.',
      searchDatabases: 'Buscar bases de datos',
      noDatabases: 'Ninguna base de datos que puedas leer coincide.',
    },
    section: {
      title: 'Bases de datos creadas en este documento',
      notShown: 'No se muestra en el documento',
      insert: 'Mostrar aquí',
    },
    types: {
      TITLE: 'Título',
      TEXT: 'Texto',
      NUMBER: 'Número',
      SELECT: 'Selección',
      MULTI_SELECT: 'Selección múltiple',
      STATUS: 'Estado',
      DATE: 'Fecha',
      PERSON: 'Persona',
      CHECKBOX: 'Casilla',
      URL: 'Enlace',
      RELATION: 'Relación',
    },
    numberFormat: {
      NUMBER: 'Número',
      INTEGER: 'Número entero',
      PERCENT: 'Porcentaje',
      BRL: 'Real (R$)',
      USD: 'Dólar estadounidense ($)',
      EUR: 'Euro (€)',
    },
    statusGroup: {
      TODO: 'Por hacer',
      IN_PROGRESS: 'En curso',
      DONE: 'Completado',
    },
    property: {
      add: 'Agregar una propiedad',
      create: 'Agregar propiedad',
      name: 'Nombre de la propiedad',
      type: 'Tipo',
      menu: 'Menú de la columna {{name}}',
      resize: 'Cambiar el ancho de {{name}}',
      numberFormat: 'Formato de número',
      editOptions: 'Editar opciones',
      editOptionsOf: 'Opciones de {{name}}',
      optionColor: 'Color de {{name}}',
      optionName: 'Nombre de la opción',
      optionGroup: 'Grupo de estado',
      removeOption: 'Quitar {{name}}',
      addOption: 'Agregar una opción',
      newOption: 'Opción {{n}}',
      removeOptionsConfirm_one: '¿Quitar {{count}} opción? Las celdas que la usan quedan vacías.',
      removeOptionsConfirm_other: '¿Quitar {{count}} opciones? Las celdas que las usan quedan vacías.',
      hide: 'Ocultar en esta vista',
      delete: 'Eliminar propiedad',
      deleteConfirm: '¿Eliminar la propiedad «{{name}}» y todos sus valores?',
      confirmLoss: '¿Cambiar el tipo de todos modos?',
      relatedDatabase: 'Base de datos relacionada',
      thisDatabase: 'Esta base de datos ({{name}})',
      twoWay: 'Mostrar la relación también en la otra base de datos',
    },
    cell: {
      searchOptions: 'Buscar opciones',
      searchOrCreate: 'Buscar o crear una opción',
      createOption: 'Crear',
      removeOption: 'Quitar {{name}}',
      searchPeople: 'Buscar personas',
      removePerson: 'Quitar a {{name}}',
      searchRows: 'Buscar filas',
      removeRelation: 'Quitar {{name}}',
      today: 'Hoy',
      clear: 'Borrar',
      empty: 'Vacío',
    },
    row: {
      new: 'Nueva',
      open: 'Abrir',
      openAsPage: 'Abrir como página',
      goToPage: 'Ir a su página',
      insertBelow: 'Insertar fila debajo',
      delete: 'Eliminar fila',
      menu: 'Acciones de {{name}}',
      actions: 'Acciones de fila',
      edited: 'Última edición de',
    },
    view: {
      tabs: 'Vistas',
      add: 'Agregar una vista',
      menu: 'Acciones de la vista {{name}}',
      rename: 'Renombrar vista',
      renamePrompt: 'Nombre de la vista',
      delete: 'Eliminar vista',
      deleteConfirm: '¿Eliminar la vista «{{name}}»? Las filas se mantienen.',
      properties: 'Propiedades',
      shown: 'Visible',
      hidden: 'Oculta',
      readerChanges: 'Tus filtros y orden se aplican solo para ti; los editores del documento cambian la vista guardada.',
      kind: {
        TABLE: 'Tabla',
        BOARD: 'Tablero',
        LIST: 'Lista',
        CALENDAR: 'Calendario',
      },
    },
    filter: {
      title: 'Filtrar',
      none: 'Sin filtros: se ven todas las filas.',
      where: 'Donde',
      and: 'y',
      property: 'Propiedad del filtro',
      operator: 'Condición',
      value: 'Valor',
      add: 'Agregar un filtro',
      remove: 'Quitar filtro',
      clear: 'Quitar filtros',
      checked: 'Marcada',
      unchecked: 'Sin marcar',
      me: 'Yo',
      op: {
        eq: 'es',
        neq: 'no es',
        contains: 'contiene',
        not_contains: 'no contiene',
        gt: 'es mayor que',
        gte: 'es al menos',
        lt: 'es menor que',
        lte: 'es como máximo',
        empty: 'está vacío',
        not_empty: 'no está vacío',
      },
      date: {
        eq: 'es el',
        lt: 'es antes de',
        gt: 'es después de',
        lte: 'es el o antes de',
        gte: 'es el o después de',
      },
    },
    sort: {
      title: 'Ordenar',
      none: 'Sin orden: las filas mantienen su orden.',
      property: 'Propiedad del orden',
      direction: 'Dirección',
      asc: 'Orden ascendente',
      desc: 'Orden descendente',
      add: 'Agregar un orden',
      remove: 'Quitar orden',
    },
    board: {
      groupBy: 'Agrupar por',
      chooseGroup: 'Elige la propiedad que forma las columnas.',
      needsGroup: 'Un tablero agrupa las filas por una propiedad de selección, estado o persona. Agrega una para usar esta vista.',
      none: 'Sin {{name}}',
      label: 'Tablero agrupado por {{name}}',
    },
    calendar: {
      dateProperty: 'Propiedad de fecha',
      chooseDate: 'Elige la propiedad de fecha que ubica las filas en el calendario.',
      needsDate: 'Un calendario ubica las filas por una propiedad de fecha. Agrega una para usar esta vista.',
      previous: 'Mes anterior',
      next: 'Mes siguiente',
      today: 'Hoy',
      by: 'Por {{name}}',
      label: 'Calendario de {{name}}',
      add: 'Agregar una fila el {{date}}',
      more: '+{{count}} más',
    },
  },
  time: {
    justNow: 'justo ahora',
  },
};
